/**
 * Canonical order schema — TypeScript interfaces and utilities for CanonicalOrderPayload.
 *
 * This is the single source of truth for the ordering session data model in the frontend.
 * All types mirror the Python CanonicalOrderPayload Pydantic model
 * (carebus_client.schemas.ordering.canonical_order) using camelCase JSON keys.
 *
 * Legacy TRF mapping functions (mapTrfToCanonical, deriveOrderType) are kept as
 * private helpers for legacyTrfPayloadToCanonical only — do not add new callers.
 */

import { EpicAuthClient } from '@/features/auth/services/EpicAuthClient'
import {
  CLINICAL_SUMMARY_LABEL_MAX_LENGTH,
  clinicalSummaryFingerprint,
  type ClinicalSummaryReview,
} from '@/pages/order/clinicalSummaryReviewTypes'
import type {
  AuthorizingProfessionalInfo,
  CategorizedHPOOption,
  ConsentFormData,
  IncidentalFindingsSelection,
  OrderFormInfoData,
  ParentFieldShape,
  PartialRequisitionFormData,
  PhysicianInfo,
  RequisitionFormData,
} from '@/types/RequisitionFormTypes'
import { CollectionType, normalizePhenotypicTrait, PhenotypicTrait, Sex, TestType } from '@/types/RequisitionFormTypes'
import type { SampleTypeOption } from '@/utils/orderFormUtils'
import { getLocalCalendarDateString, parseDateOnlyToLocalDate } from '@/utils/orderFormUtils'
// GEN-3393: `comparatorWriteAdapter` imports from this module are `import type`
// only, so importing its runtime helpers here does not create a require cycle.
import { orderTypeFromComparators } from './comparatorReadAdapter'
import { normalizeInboundParentComparators, parentSideForComparator } from './comparatorWriteAdapter'

export const CANONICAL_SCHEMA_VERSION = '1.2'

/**
 * Schema version bump policy:
 *
 * - PATCH (1.0 → 1.1): new optional fields added; old payloads remain valid.
 *   Increment the minor version in CANONICAL_SCHEMA_VERSION and add a comment
 *   in legacyTrfPayloadToCanonical if back-fill is needed.
 *
 * - MAJOR (1.x → 2.0): rename or remove a field, or change a field's type.
 *   Requires a migration in legacyTrfPayloadToCanonical and a Pydantic version
 *   bump in CareBus_Client canonical_order.py + provider-api test fixtures.
 *   Always coordinate across TS + Python in the same PR.
 */

// ─── Canonical shape interfaces ─────────────────────────────────────────────

export interface CanonicalAddress {
  zip?: string | null
  city?: string | null
  state?: string | null
  street?: string | null
  country?: string | null
}

/**
 * Shipping address for a collection-kit request. Distinct from CanonicalAddress:
 * carries `type` (RESIDENTIAL | HOSPITAL) and `street2`, matching the fields the
 * provider-api producer flattens into the kit_shipment.requested event.
 * Mirrors `KitShipmentAddress` in CareBus_Client canonical_order.py.
 */
export interface KitShipmentAddress {
  type?: string | null
  street?: string | null
  street2?: string | null
  city?: string | null
  state?: string | null
  zip?: string | null
  country?: string | null
}

/**
 * Per-comparator request to ship a genetic-test collection kit. Presence of this
 * object on a comparator makes provider-api emit a kit_shipment.requested event
 * for that comparator. `location` is HOME | CLINIC. Mirrors `KitShipmentRequest`
 * in CareBus_Client canonical_order.py.
 */
export interface KitShipmentRequested {
  location?: string | null
  orderItemExternalKey?: string | null
  shipmentAddress?: KitShipmentAddress | null
}

/**
 * Kit type names as seeded in IMDB `kit_type`. The UI never lets the user pick
 * these directly — they are DERIVED from the selected sample type + kit
 * destination, mirroring provider-api `_derive_kit_type` so the SPA preview
 * matches what the backend will actually create.
 */
export type DerivedKitType = 'BUCCAL_DIRECT_TO_PATIENT' | 'BUCCAL_DIRECT_TO_BUSINESS' | 'BLOOD_DIRECT_TO_BUSINESS'

/**
 * Derive the kit type from the comparator's sample type and the kit
 * destination, mirroring provider-api ``_derive_kit_type``:
 *
 * - HOME (direct-to-patient): always a buccal collection kit.
 * - CLINIC (direct-to-business): whole blood → blood kit; buccal/saliva →
 *   buccal kit; anything else (unknown / no sample type) → null.
 *
 * Returns null when a kit type cannot be determined (never guesses a SKU),
 * matching the backend's skip-rather-than-fabricate contract.
 */
export function deriveKitType(
  sampleType: SampleTypeOption | null | undefined,
  location: string | null | undefined
): DerivedKitType | null {
  const loc = (location ?? '').trim().toUpperCase()
  if (loc === 'HOME') {
    return 'BUCCAL_DIRECT_TO_PATIENT'
  }
  if (loc === 'CLINIC') {
    if (sampleType === 'whole-blood') {
      return 'BLOOD_DIRECT_TO_BUSINESS'
    }
    if (sampleType === 'buccal-swab' || sampleType === 'saliva') {
      return 'BUCCAL_DIRECT_TO_BUSINESS'
    }
    return null
  }
  return null
}

/** Human-readable label for a derived kit type (for the read-only preview). */
export function kitTypeLabel(kitType: DerivedKitType): string {
  switch (kitType) {
    case 'BUCCAL_DIRECT_TO_PATIENT':
      return 'Buccal swab kit — shipped to patient'
    case 'BUCCAL_DIRECT_TO_BUSINESS':
      return 'Buccal swab kit — shipped to clinic'
    case 'BLOOD_DIRECT_TO_BUSINESS':
      return 'Whole blood kit — shipped to clinic'
  }
}

/**
 * Accepts two vocabularies simultaneously while the Comparator + Relationship
 * Model migration is in flight (IMDB design doc §3.2). Legacy lower_snake_case
 * values stay valid for stored payloads until the Phase E one-time string
 * rewrite; the UPPER_CASE vocabulary is the canonical target for
 * `comparators[]` rows.
 *
 * Mirrors Python `FamilyMemberRelationship` in
 * `CareBus_Client/src/carebus_client/schemas/ordering/canonical_order.py`.
 */
export type FamilyMemberRelationship =
  // Legacy (retained until Phase E)
  | 'proband'
  | 'bio_mother'
  | 'bio_father'
  | 'sibling'
  | 'other'
  | 'legal_guardian'
  // Canonical core
  | 'PROBAND'
  | 'MOTHER'
  | 'FATHER'
  | 'LEGAL_GUARDIAN'
  | 'OTHER'
  | 'SIBLING'
  // Multiple birth
  | 'TWIN_SIBLING'
  | 'MULTIPLE_BIRTH_SIBLING'
  // Half-sibling lineage
  | 'HALF_SIBLING_MATERNAL'
  | 'HALF_SIBLING_PATERNAL'
  // Grandparents
  | 'GRANDMOTHER_MATERNAL'
  | 'GRANDFATHER_MATERNAL'
  | 'GRANDMOTHER_PATERNAL'
  | 'GRANDFATHER_PATERNAL'
  // Great-grandparents
  | 'GREAT_GRANDMOTHER_MATERNAL'
  | 'GREAT_GRANDFATHER_MATERNAL'
  | 'GREAT_GRANDMOTHER_PATERNAL'
  | 'GREAT_GRANDFATHER_PATERNAL'
  // Aunts / uncles / cousins
  | 'AUNT_MATERNAL'
  | 'UNCLE_MATERNAL'
  | 'AUNT_PATERNAL'
  | 'UNCLE_PATERNAL'
  | 'COUSIN_MATERNAL'
  | 'COUSIN_PATERNAL'
  // Descendants
  | 'NIECE'
  | 'NEPHEW'
  | 'CHILD'
  | 'GRANDCHILD'

/**
 * Whether a relationship is a guardian/signer-only, NON-comparator role — i.e.
 * a legal guardian (`LEGAL_GUARDIAN`) or the catch-all `OTHER`. Neither is a
 * selectable comparator relationship (both are absent from
 * `API_SUPPORTED_RELATIONSHIP_VALUES`), so they can only enter `comparators[]`
 * via rehydration, never via a picker. Both are guardian relationship values,
 * and pre-GEN-2613 a guardian's relationship was collapsed to `'other'` at
 * submit — so an `OTHER` / `LEGAL_GUARDIAN` comparator row is a guardian /
 * consent-signer artifact (the guardian's real home is
 * {@link CanonicalOrderPayload.guardianContact}), not a sequencing comparator.
 *
 * Biological MOTHER / FATHER are excluded here on purpose: they can be guardians
 * AND legitimate parent comparators, so they must remain in `comparators[]`.
 * Every genuine family relationship (SIBLING, aunts/uncles, grandparents,
 * cousins, niece/nephew, child/grandchild, twins) is likewise a real comparator
 * and is left in place.
 *
 * Used at the rehydration boundary to drop guardian/signer rows the server
 * persisted into `requisitionFormData.comparators[]`, so they never surface as
 * a comparator/family-member for comparison after reload.
 */
export function isGuardianOnlyComparatorRelationship(relationship: unknown): boolean {
  if (typeof relationship !== 'string') return false
  const u = relationship.trim().toUpperCase()
  return u === 'LEGAL_GUARDIAN' || u === 'OTHER'
}

/**
 * Drop any guardian/signer-only rows (legal guardian, or the catch-all `OTHER`)
 * from a canonical payload's `comparators[]`. Such a row is captured on the
 * dedicated {@link CanonicalOrderPayload.guardianContact} field, never as a
 * sequencing comparator — but the server may persist a `LEGAL_GUARDIAN` /
 * `OTHER` row into the comparator list, and various rehydration paths
 * (legacy-TRF reconstruction, sparse-canonical merge, RFD overlay) can carry it
 * through. Applying this at the hydration boundary guarantees such a row never
 * surfaces as a comparator regardless of which path produced it. Returns the
 * payload unchanged when there is nothing to strip (preserving the original
 * `comparators` reference). See {@link isGuardianOnlyComparatorRelationship}.
 */
export function stripGuardianComparators(canonical: CanonicalOrderPayload): CanonicalOrderPayload {
  if (!Array.isArray(canonical.comparators)) return canonical
  const filtered = canonical.comparators.filter(
    (c) => !(c != null && isGuardianOnlyComparatorRelationship(c.relationship))
  )
  if (filtered.length === canonical.comparators.length) return canonical
  return { ...canonical, comparators: filtered.length > 0 ? filtered : null }
}

export type Zygosity = 'MONOZYGOTIC' | 'DIZYGOTIC' | 'UNKNOWN' | 'NOT_APPLICABLE'

/** Multi-birth grouping (twins, triplets, ...) for a family-member row. Forward-compat in Phase B. */
export interface ComparatorMultipleBirth {
  /** UUID grouping co-twins/triplets within one family. */
  setId: string
  /** FHIR Patient.multipleBirthInteger. */
  birthOrder?: number | null
  zygosity: Zygosity
}

/** Identity block for a comparator row (proband or non-proband). */
export interface ComparatorDemographic {
  firstName?: string | null
  lastName?: string | null
  middleName?: string | null
  dateOfBirth?: string | null
  /**
   * Sex assigned at birth (`'male' | 'female' | 'ambiguous'`; empty/`null` when
   * unspecified). Mirrors `CanonicalPatient.sex` — comparators are now
   * independent of the fixed mother/father binary, so each carries its own sex
   * assigned at birth (propagated to the TRF Section J sex checkboxes).
   */
  sex?: string | null
  mrn?: string | null
  email?: string | null
  phone?: string | null
  externalPatientId?: string | null
  demographicId?: string | null
}

/** Specimen block when this comparator is being sequenced. */
export interface ComparatorSpecimen {
  specimenId?: string | null
  collectionType?: string | null
}

/** Per-comparator secondary-consent flags (parallels CanonicalParentInfo today). */
export interface ComparatorSecondaryConsent {
  includeIncidentalFindings?: boolean | null
  newYorkSampleRetentionOptIn?: boolean | null
  researchContactConsent?: boolean | null
}

/**
 * One family-member comparator row (replaces fixed `mother` / `father` slots).
 *
 * Additive in Phase B (GEN-2993): producers MAY emit `comparators[]` alongside
 * the legacy `mother` / `father` slots; the imdb_client adapter (Phase B.3 /
 * GEN-3005) keeps both shapes in sync until Phase G removes the legacy slots.
 */
export interface Comparator {
  id?: string | null
  relationship: FamilyMemberRelationship
  demographic?: ComparatorDemographic | null
  specimen?: ComparatorSpecimen | null
  isPatient?: boolean | null
  isPrimaryGuardian?: boolean | null
  isTestingComparator?: boolean | null
  secondaryConsent?: ComparatorSecondaryConsent | null
  kitShipmentRequested?: KitShipmentRequested | null
  multipleBirth?: ComparatorMultipleBirth | null
  phenotypicTrait?: string | null
  icd10Terms?: string | null
  clinicalNote?: string | null
  hpoTerms?: unknown[]
}

export interface CanonicalEhrContext {
  fhirPatientId?: string | null
  fhirUserId?: string | null
  ehrEncounterId?: string | null
  mrnIdentifierSystem?: string | null
}

export interface CanonicalPersonName {
  lastName?: string | null
  firstName?: string | null
  middleName?: string | null
}

export interface CanonicalPatient {
  externalPatientId?: string | null
  mrn?: string | null
  sex?: string | null
  lastName?: string | null
  firstName?: string | null
  middleName?: string | null
  dateOfBirth?: string | null
  collectionType?: string | null
  specimenId?: string | null
  /** US Core race display text (from FHIR us-core-race on EHR launch). */
  race?: string | null
  /** US Core ethnicity display text (from FHIR us-core-ethnicity on EHR launch). */
  ethnicity?: string | null
  /** Proband incidental findings (distinct from comparator parent rows). */
  incidentalFindingsSelection?: IncidentalFindingsSelection | null
  newYorkSampleRetentionOptIn?: boolean | null
  researchContactConsent?: boolean | null
  /** Forward-compat: populated when the proband is a twin / multiple-birth member (Phase C+). */
  multipleBirth?: ComparatorMultipleBirth | null
}

export interface CanonicalInstitution {
  id?: string | null
  name?: string | null
  address?: CanonicalAddress | null
}

export interface CanonicalMedicalProvider {
  id?: string | null
  npi?: string | null
  name?: CanonicalPersonName | null
  email?: string | null
  phone?: string | null
  roleTitle?: string | null
  /** Post-nominal credentials / titles (e.g. "MD", "DO", "MS, CGC"). */
  credentials?: string | null
}

export interface CanonicalMedicalProfessionalInfo {
  npi?: string | null
  name?: string | null
  email?: string | null
}

export interface CanonicalProviders {
  orderingProvider?: CanonicalMedicalProvider | null
  patientPhysician?: CanonicalMedicalProvider | null
  authorizingProvider?: CanonicalMedicalProvider | null
  medicalProfessionalInfo?: CanonicalMedicalProfessionalInfo | null
  authorizingProfessionalInfo?: CanonicalMedicalProfessionalInfo | null
}

export interface CanonicalOrderSelection {
  /**
   * The sequencing test / panel code (e.g. "SINGLETON_PROBAND_ONLY").
   *
   * **Policy: this field is the single source of truth for test identity.**
   *  - UI renders and user-facing selection store this value directly.
   *  - Session PATCH always persists this value as-is.
   *  - On submit, OrderIntent.tests[0].code = selectedOption.
   *
   * Distinct from CanonicalOrderPayload.orderType, which encodes FAMILY STRUCTURE
   * (PROBAND_ONLY, FAMILY_DUO_MOTHER, etc.) and is used by map_intent_to_submitted_event
   * only as a fallback when tests[] is empty.  Always populate selectedOption before
   * calling submitOrderingSession.
   */
  selectedOption?: string | null
  labName?: string | null
  /** CMS CLIA certificate number for the wet lab (from org ``WET_LAB.cliaLabId``). */
  cliaLabId?: string | null
  encounterCsn?: string | null
}

export interface CanonicalFamily {
  familyHistory?: string | null
  hasParent?: boolean | null
  hasGrandparent?: boolean | null
  hasSibling?: boolean | null
  hasCousin?: boolean | null
}

export interface CanonicalParentInfo {
  id?: string | null
  parentType?: string | null
  parentFirstName?: string | null
  parentLastName?: string | null
  parentDateOfBirth?: string | null
  parentSex?: string | null
  parentMRN?: string | null
  parentEmail?: string | null
  parentPhone?: string | null
  collectionType?: string | null
  specimenId?: string | null
  demographicId?: string | null
  icd10Terms?: string | null
  /** Free-text clinical notes for this parent (when clinically affected). */
  clinicalNote?: string | null
  /** True when this parent is marked as primary guardian (syncs to guardian contact when applicable). */
  isPrimaryGuardian?: boolean | null
  /** Comparator parent incidental findings: true = opt-in, false = opt-out, null/omit = not chosen. */
  includeIncidentalFindings?: boolean | null
  /** Comparator parent NY sample retention opt-in when that block is enabled for parents. */
  newYorkSampleRetentionOptIn?: boolean | null
  /** Comparator parent consented to be contacted for research participation. */
  researchContactConsent?: boolean | null
  phenotypicTrait?: string | null
  hpoTerms?: unknown[]
}

export interface CanonicalReason {
  icd10Diagnosis?: string | null
  userInteracted?: boolean | null
  clinicalDiagnosis?: CategorizedHPOOption[]
  clinicalSummaryReview?: ClinicalSummaryReview | null
}

export interface CanonicalClinical {
  clinicalNote?: string | null
  optOutClinicianNotes?: boolean | null
  differentialDiagnosis?: string | null
  priorGeneticTesting?: string | null
}

/** Legal guardian / contact for the patient (optional; not parent testing). */
export interface CanonicalGuardianContact {
  /** True when guardian contact is explicitly marked unavailable (vs leaving section empty). */
  contactNotAvailable?: boolean | null
  firstName?: string | null
  lastName?: string | null
  /** ISO YYYY-MM-DD from date input. */
  dateOfBirth?: string | null
  email?: string | null
  phone?: string | null
  /** Stored value from relationship select (UPPER_SNAKE). */
  relationshipToPatient?: string | null
}

/**
 * Allowed guardian / consenter–patient relationship values for JSON / API
 * (UPPER_SNAKE).
 *
 * The first four are the core consent-driving values the provider-api branches
 * on today (MOTHER/FATHER/LEGAL_GUARDIAN collapse to their legacy lowercase
 * tokens at submit; OTHER → 'other'). The remaining values are the extended
 * IMDB `family_member.relationship` lineage vocabulary (carebus-client 1.43.0):
 * the client no longer rejects them and they round-trip verbatim as UPPER_CASE
 * on `primaryConsent.familyMember.relationship`, even though the API does not
 * yet special-case them for mother/father/guardian routing.
 */
export const GUARDIAN_RELATIONSHIP_VALUES = [
  'MOTHER',
  'FATHER',
  'LEGAL_GUARDIAN',
  'OTHER',
  'SIBLING',
  'TWIN_SIBLING',
  'MULTIPLE_BIRTH_SIBLING',
  'HALF_SIBLING_MATERNAL',
  'HALF_SIBLING_PATERNAL',
  'GRANDMOTHER_MATERNAL',
  'GRANDFATHER_MATERNAL',
  'GRANDMOTHER_PATERNAL',
  'GRANDFATHER_PATERNAL',
  'GREAT_GRANDMOTHER_MATERNAL',
  'GREAT_GRANDFATHER_MATERNAL',
  'GREAT_GRANDMOTHER_PATERNAL',
  'GREAT_GRANDFATHER_PATERNAL',
  'AUNT_MATERNAL',
  'UNCLE_MATERNAL',
  'AUNT_PATERNAL',
  'UNCLE_PATERNAL',
  'COUSIN_MATERNAL',
  'COUSIN_PATERNAL',
  'NIECE',
  'NEPHEW',
  'CHILD',
  'GRANDCHILD',
] as const

export type GuardianRelationshipValue = (typeof GUARDIAN_RELATIONSHIP_VALUES)[number]

const GUARDIAN_RELATIONSHIP_LABELS: Record<GuardianRelationshipValue, string> = {
  MOTHER: 'Biological Mother',
  FATHER: 'Biological Father',
  LEGAL_GUARDIAN: 'Legal Guardian',
  OTHER: 'Other',
  SIBLING: 'Sibling',
  TWIN_SIBLING: 'Twin Sibling',
  MULTIPLE_BIRTH_SIBLING: 'Multiple-birth Sibling',
  HALF_SIBLING_MATERNAL: 'Half-sibling (maternal)',
  HALF_SIBLING_PATERNAL: 'Half-sibling (paternal)',
  GRANDMOTHER_MATERNAL: 'Grandmother (maternal)',
  GRANDFATHER_MATERNAL: 'Grandfather (maternal)',
  GRANDMOTHER_PATERNAL: 'Grandmother (paternal)',
  GRANDFATHER_PATERNAL: 'Grandfather (paternal)',
  GREAT_GRANDMOTHER_MATERNAL: 'Great-grandmother (maternal)',
  GREAT_GRANDFATHER_MATERNAL: 'Great-grandfather (maternal)',
  GREAT_GRANDMOTHER_PATERNAL: 'Great-grandmother (paternal)',
  GREAT_GRANDFATHER_PATERNAL: 'Great-grandfather (paternal)',
  AUNT_MATERNAL: 'Aunt (maternal)',
  UNCLE_MATERNAL: 'Uncle (maternal)',
  AUNT_PATERNAL: 'Aunt (paternal)',
  UNCLE_PATERNAL: 'Uncle (paternal)',
  COUSIN_MATERNAL: 'Cousin (maternal)',
  COUSIN_PATERNAL: 'Cousin (paternal)',
  NIECE: 'Niece',
  NEPHEW: 'Nephew',
  CHILD: 'Child',
  GRANDCHILD: 'Grandchild',
}

/** Prior UI allowlist; still shown if present in stored payloads. */
const LEGACY_GUARDIAN_RELATIONSHIP_LABELS: Record<string, string> = {
  PARENT: 'Parent',
  GRANDPARENT: 'Grandparent',
  STEP_PARENT: 'Step-parent',
}

/**
 * The four consent-driving values the provider-api branches on today and the
 * only relationships the legacy mother/father-only flow ever offered. The
 * legacy Guardian Contact picker is restricted to these; the extended lineage
 * vocabulary is reserved for the newer (side-nav / comparator-decoupled) flow.
 */
export const LEGACY_GUARDIAN_RELATIONSHIP_VALUES: readonly GuardianRelationshipValue[] = [
  'MOTHER',
  'FATHER',
  'LEGAL_GUARDIAN',
  'OTHER',
]

/**
 * Guardian relationships the provider-api accepts on submit. The API validates
 * `primaryConsent.familyMember.relationship` against a strict literal set, so
 * the extended guardian picker is restricted to the intersection of the
 * guardian vocabulary and that allowlist — plus the two consent-routing tokens
 * the API has always understood: `LEGAL_GUARDIAN` (→ `legal_guardian`) and
 * `OTHER` (→ `other`), which a guardian who is not a blood relative requires.
 * `Daughter` / `Son` from the API list have no guardian token (the guardian is
 * never a descendant of the proband) and are omitted.
 */
export const API_SUPPORTED_GUARDIAN_RELATIONSHIP_VALUES: readonly GuardianRelationshipValue[] = [
  'MOTHER',
  'FATHER',
  'LEGAL_GUARDIAN',
  'OTHER',
  'SIBLING',
  'AUNT_PATERNAL',
  'UNCLE_PATERNAL',
  'GRANDFATHER_PATERNAL',
  'GRANDMOTHER_PATERNAL',
  'AUNT_MATERNAL',
  'UNCLE_MATERNAL',
  'GRANDFATHER_MATERNAL',
  'GRANDMOTHER_MATERNAL',
]

const buildGuardianRelationshipOptions = (
  values: readonly GuardianRelationshipValue[]
): ReadonlyArray<{ value: GuardianRelationshipValue; label: string }> => {
  const priority: GuardianRelationshipValue[] = ['MOTHER', 'FATHER', 'LEGAL_GUARDIAN', 'OTHER']
  const orderedPriority = priority.filter((v) => values.includes(v))
  const rest = values
    .filter((v) => !priority.includes(v))
    .sort((a, b) => GUARDIAN_RELATIONSHIP_LABELS[a].localeCompare(GUARDIAN_RELATIONSHIP_LABELS[b]))
  return [...orderedPriority, ...rest].map((value) => ({
    value,
    label: GUARDIAN_RELATIONSHIP_LABELS[value],
  }))
}

/**
 * Full picker-friendly options (newer / side-nav flow), restricted to the
 * relationships the provider-api accepts on submit
 * ({@link API_SUPPORTED_GUARDIAN_RELATIONSHIP_VALUES}). Selecting an extended
 * lineage value the API does not recognize produced a pydantic `literal_error`
 * on `primaryConsent.familyMember.relationship` (e.g. "Input should be
 * 'Proband', 'Mother', 'Father', … or 'Maternal Grandmother'"). MOTHER /
 * FATHER / LEGAL_GUARDIAN / OTHER stay first (the consent-routing values a
 * guardian needs); the API-supported extended lineage values follow, sorted
 * alphabetically by label.
 */
export const GUARDIAN_RELATIONSHIP_OPTIONS = buildGuardianRelationshipOptions(
  API_SUPPORTED_GUARDIAN_RELATIONSHIP_VALUES
)

/**
 * Restricted picker options for the legacy mother/father-only Guardian Contact
 * flow — the four values that existed before the carebus-client 1.43.0 lineage
 * expansion.
 */
export const LEGACY_GUARDIAN_RELATIONSHIP_OPTIONS = buildGuardianRelationshipOptions(
  LEGACY_GUARDIAN_RELATIONSHIP_VALUES
)

export function formatGuardianRelationshipLabel(value: string | null | undefined): string | undefined {
  if (!value?.trim()) return undefined
  if (isGuardianRelationshipValue(value)) return GUARDIAN_RELATIONSHIP_LABELS[value]
  return LEGACY_GUARDIAN_RELATIONSHIP_LABELS[value] ?? value
}

export function isGuardianRelationshipValue(v: string): v is GuardianRelationshipValue {
  return (GUARDIAN_RELATIONSHIP_VALUES as readonly string[]).includes(v)
}

/** Map guardian UI relationship (UPPER_SNAKE) to canonical primaryConsent.familyMember.relationship.
 *
 * The IMDB family_member.relationship column is a varchar (no longer a Postgres
 * enum), so we can round-trip the original UI distinction by sending
 * 'legal_guardian' literally instead of collapsing every non-MOTHER/FATHER
 * value to 'other'. Without this, LEGAL_GUARDIAN was indistinguishable from
 * OTHER on reload (see GEN-2613).
 *
 * carebus-client 1.43.0 loosened `primaryConsent.familyMember.relationship` to
 * a permissive free-form string, but the provider-api still validates the value
 * against a strict allowlist ({@link API_SUPPORTED_GUARDIAN_RELATIONSHIP_VALUES})
 * on submit — sending an unsupported literal fails with a pydantic
 * `literal_error`. The three core consent tokens keep their legacy lowercase
 * spelling the API routing understands (MOTHER → 'bio_mother', FATHER →
 * 'bio_father', LEGAL_GUARDIAN → 'legal_guardian'); the API-supported extended
 * lineage values (SIBLING, grandparents, aunts/uncles) round-trip verbatim as
 * their UPPER_CASE token. Anything else — including legacy stored values that
 * are no longer offered in the picker (COUSIN_*, TWIN_SIBLING, great-grandparents,
 * …) — collapses to 'other' so a preserved-but-unsupported relationship can
 * never be submitted and rejected by the API.
 */
export function guardianRelationshipToFamilyMember(
  relationshipToPatient: string | null | undefined
): FamilyMemberRelationship {
  const u = (relationshipToPatient ?? '').trim().toUpperCase()
  if (u === 'MOTHER') return 'bio_mother'
  if (u === 'FATHER') return 'bio_father'
  if (u === 'LEGAL_GUARDIAN') return 'legal_guardian'
  if (u === 'OTHER' || u === '') return 'other'
  // Only forward extended lineage values the provider-api accepts; anything
  // unsupported (legacy stored values preserved in the picker, unknown strings)
  // collapses to 'other' to avoid a submit-time `literal_error`.
  if ((API_SUPPORTED_GUARDIAN_RELATIONSHIP_VALUES as readonly string[]).includes(u)) {
    return u as FamilyMemberRelationship
  }
  return 'other'
}

export interface PrimaryConsentPractitioner {
  npi?: string | null
  name?: string | null
  email?: string | null
  signature?: string | null
  signatureDate?: string | null
  consentedText?: string | null
}

export interface PrimaryConsentFamilyMember {
  /** Guardian / signer identity (submit + IMDB; optional until captured). */
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  phone?: string | null
  relationship?: FamilyMemberRelationship | null
  relationshipDetail?: string | null
  /** ISO YYYY-MM-DD when merged from guardian contact or captured on PCM. */
  dateOfBirth?: string | null
  status?: string | null
  envelopeId?: string | null
  isSigningComplete?: boolean | null
  signature?: string | null
  signatureDate?: string | null
  signerDisplayName?: string | null
}

export interface PrimaryConsent {
  practitioner?: PrimaryConsentPractitioner | null
  familyMember?: PrimaryConsentFamilyMember | null
  /** IMDB primary_consent row discriminator when configured (e.g. ATTESTATION). */
  consentType?: string | null
  /** Stable config key for primary_consent.primary_consent_key. */
  primaryConsentKey?: string | null
}

export interface CanonicalOrderPayload {
  schemaVersion: string
  orderType: string
  /**
   * The organization ordering site chosen for this order (GEN-5738), a plain
   * ``siteName`` string from ``ui_config.organizationSiteInformation`` — Phase 1
   * identifies a site by name (a stable code is Phase 2 / GEN-5739). Required at
   * draft-create and allowlist-validated on create/patch by the provider-api for
   * organizations with ``trackOrderSite`` enabled; omitted/``null`` for every
   * other tenant. Mirrors ``CanonicalOrderPayload.ordering_site`` in
   * CareBus_Client canonical_order.py.
   */
  orderingSite?: string | null
  patient?: CanonicalPatient | null
  institution?: CanonicalInstitution | null
  providers?: CanonicalProviders | null
  order?: CanonicalOrderSelection | null
  family?: CanonicalFamily | null
  /**
   * Generic comparator list — the single source of truth for family members
   * (proband + MOTHER / FATHER / other relatives). GEN-3393 removed the fixed
   * `mother` / `father` slots; any residual slot data on an inbound payload is
   * folded into this array at the hydration boundary
   * (`normalizeInboundParentComparators`).
   */
  comparators?: Comparator[] | null
  /** Optional guardian contact; persisted in session JSON (camelCase). */
  guardianContact?: CanonicalGuardianContact | null
  reason?: CanonicalReason | null
  clinical?: CanonicalClinical | null
  primaryConsent?: PrimaryConsent | null
  ehrContext?: CanonicalEhrContext | null
  version?: number | null
  /**
   * Cell D — SeqOne HPO lock. When ``true``, the SPA disables HPO editing for
   * existing/persisted members (proband + already-saved comparators) on
   * ``inflection_test`` orders (see ``canEditExistingMemberHpo`` in
   * ``src/config/uiConfig.ts``). Missing/``undefined`` is treated as NOT locked.
   *
   * TODO(Cell C): wire this to the provider-api order read model so it reflects
   * the authoritative backend lock. Until the backend surfaces it, this stays
   * ``undefined`` (unlocked) and the SPA never invents a fetch for it.
   */
  seqoneHPOLock?: boolean | null
}

/**
 * Legacy parent-slot fields, accepted **only** at the hydration boundary for
 * backward-compatibility with payloads / ordering sessions persisted before
 * GEN-3393 dropped the slots. `normalizeInboundParentComparators` folds any
 * populated slot into `comparators[]` and strips these keys, so they never
 * reach Redux state or the rest of the app.
 */
export interface LegacyParentSlots {
  mother?: CanonicalParentInfo | null
  father?: CanonicalParentInfo | null
}

/** A canonical payload as it may arrive from the wire / persisted sessions: the
 * clean shape plus the optional legacy parent slots handled at hydration. */
export type CanonicalOrderPayloadInbound = CanonicalOrderPayload & LegacyParentSlots

/**
 * IMDB requires a non-null guardian `date_of_birth`, so the order pipeline persists a
 * `1900-01-01` placeholder when the guardian DOB was never captured (see the imdb_client
 * demographics step). That sentinel must never surface in the editable client state:
 * date fields should render the empty `mm/dd/yyyy` placeholder, not `01/01/1900`.
 */
export const PLACEHOLDER_DOB = '1900-01-01'

/** True when a DOB string is the IMDB "missing DOB" sentinel (compared date-only). */
export function isPlaceholderDob(value: string | null | undefined): boolean {
  const t = value?.trim()
  if (!t) return false
  return t.slice(0, 10) === PLACEHOLDER_DOB
}

/**
 * Strip the IMDB placeholder DOB (`1900-01-01`) from a hydrated canonical order so it never
 * reaches modals, display cards, or change-detection baselines. Returns the same reference
 * when nothing changes to avoid needless re-renders / spurious dirty flags.
 *
 * GEN-3393: parent state lives on `comparators[]` (no fixed `mother` / `father` slots), so
 * the placeholder DOB is stripped from each comparator's `demographic.dateOfBirth` here.
 */
export function stripPlaceholderDobsFromCanonical(order: CanonicalOrderPayload): CanonicalOrderPayload {
  let changed = false
  const next: CanonicalOrderPayload = { ...order }

  if (order.guardianContact && isPlaceholderDob(order.guardianContact.dateOfBirth)) {
    next.guardianContact = { ...order.guardianContact, dateOfBirth: null }
    changed = true
  }
  if (
    Array.isArray(order.comparators) &&
    order.comparators.some((c) => isPlaceholderDob(c?.demographic?.dateOfBirth))
  ) {
    next.comparators = order.comparators.map((c) =>
      c && isPlaceholderDob(c.demographic?.dateOfBirth)
        ? { ...c, demographic: { ...c.demographic, dateOfBirth: null } }
        : c
    )
    changed = true
  }
  if (order.patient && isPlaceholderDob(order.patient.dateOfBirth)) {
    next.patient = { ...order.patient, dateOfBirth: null }
    changed = true
  }
  if (order.primaryConsent?.familyMember && isPlaceholderDob(order.primaryConsent.familyMember.dateOfBirth)) {
    next.primaryConsent = {
      ...order.primaryConsent,
      familyMember: { ...order.primaryConsent.familyMember, dateOfBirth: null },
    }
    changed = true
  }

  return changed ? next : order
}

/**
 * Coerce `isPrimaryGuardian` on parent `comparators[]` rows to a real boolean
 * before PATCH/create session, and enforce the single-primary-guardian
 * invariant directly across the comparator list.
 *
 * Rule (MOTHER/FATHER migration P5):
 *   • For each parent comparator (relationship MOTHER/FATHER, including the
 *     legacy `bio_mother`/`bio_father` literals retained at the inbound
 *     boundary), coerce `isPrimaryGuardian` to a real boolean. JSON omits
 *     `undefined`; server merge + `exclude_none` then never applies the flag,
 *     so `true` from the UI could fail to persist when prior state had no key.
 *   • When two or more parent comparators are present, exactly one carries
 *     `isPrimaryGuardian: true`. If multiple are flagged, keep the LAST
 *     flagged row (preserves the historical "father wins when both" trio
 *     contract because OrderStart appends mother before father). If none
 *     are flagged, default to the FIRST parent comparator (preserves the
 *     historical "default mother when neither" trio contract for the same
 *     reason).
 *
 * The list-driven rule is the single primary-guardian enforcement seam; it
 * aligns with the slice-side enforcement in `applyComparatorUpsert`
 * (canonicalOrderSlice.ts §single-primary).
 */
export function normalizeCanonicalOrderForSessionPersist(order: CanonicalOrderPayload): CanonicalOrderPayload {
  const next: CanonicalOrderPayload = { ...order }
  const review = order.reason?.clinicalSummaryReview
  if (review) {
    // Wire format must match the clinical note on this PATCH: provider-api rejects
    // a review whose fingerprint/citations do not align with `clinicalNote`, so the
    // wire copy always carries this note's fingerprint.
    const summary = typeof order.clinical?.clinicalNote === 'string' ? order.clinical.clinicalNote : ''
    const summaryFingerprint = clinicalSummaryFingerprint(summary)
    // The merge may not have caught up with the note yet (live extraction still in
    // flight, or a surface that edits the note without re-extracting). Stamping this
    // note's fingerprint on a review merged against earlier text would make the
    // persisted review look reviewed once it is hydrated again, so the rows it never
    // checked against this note go back to the clinician as questions and lose the
    // offsets that belong to the old text. Submit stays blocked across a reload.
    const unmerged = review.summaryFingerprint !== summaryFingerprint
    next.reason = {
      ...order.reason,
      clinicalSummaryReview: {
        schemaVersion: 1,
        writerMode: 'merged-review-v1',
        summaryFingerprint,
        terms: Object.fromEntries(
          Object.entries(review.terms).map(([code, term]) => [
            code,
            {
              origin: term.origin,
              categoryOwner: term.categoryOwner,
              // A removed term stays removed: provider-api requires the removed set to
              // equal `tombstones`.
              reviewState: unmerged && term.reviewState !== 'removed' ? 'needs-input' : term.reviewState,
              citations: unmerged
                ? []
                : term.citations
                    .filter(
                      ({ begin, end }) =>
                        Number.isInteger(begin) &&
                        Number.isInteger(end) &&
                        begin >= 0 &&
                        begin < end &&
                        end <= summary.length
                    )
                    .map(({ begin, end }) => ({ begin, end })),
              ...(term.label?.trim() ? { label: term.label.slice(0, CLINICAL_SUMMARY_LABEL_MAX_LENGTH) } : {}),
            },
          ])
        ),
        tombstones: [...review.tombstones],
      },
    }
  }
  if (!Array.isArray(next.comparators) || next.comparators.length === 0) {
    return next
  }

  const isParentRelationship = (r: unknown) =>
    r === 'MOTHER' || r === 'FATHER' || r === 'bio_mother' || r === 'bio_father'

  let comparators = next.comparators.map((c) =>
    c && isParentRelationship(c.relationship) ? { ...c, isPrimaryGuardian: c.isPrimaryGuardian === true } : c
  )

  const parentIdxs: number[] = []
  for (let i = 0; i < comparators.length; i++) {
    const c = comparators[i]
    if (c && isParentRelationship(c.relationship)) parentIdxs.push(i)
  }

  if (parentIdxs.length >= 2) {
    const flagged = parentIdxs.filter((i) => comparators[i].isPrimaryGuardian === true)
    const chosen = flagged.length > 0 ? flagged[flagged.length - 1] : parentIdxs[0]
    comparators = comparators.map((c, i) => (parentIdxs.includes(i) ? { ...c, isPrimaryGuardian: i === chosen } : c))
  }

  next.comparators = comparators
  return next
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Map the post-guardian-path mother/father presence flags computed inside
 * the inbound TRF/canonical mappers below to the 4-string tier contract
 * (`PROBAND_ONLY` / `FAMILY_DUO_MOTHER` / `FAMILY_DUO_FATHER` / `FAMILY_TRIO`).
 *
 * Internal helper for `deriveFamilyStructureOrderTypeFromRfd` /
 * `deriveFamilyStructureOrderTypeFromCanonical` only — those two paths derive
 * the booleans by looking at concrete RFD/comparator slot data plus
 * guardian-path adjustments, so the comparator-set-based
 * {@link orderTypeFromComparators} (which considers ALL comparators on the
 * list verbatim) is not equivalent here. P12 (MOTHER/FATHER migration):
 * replaces the previously exported `computeOrderType(hasMother, hasFather)`.
 */
function tierFromParentFlags(hasMother: boolean, hasFather: boolean): string {
  if (hasMother && hasFather) return 'FAMILY_TRIO'
  if (hasMother) return 'FAMILY_DUO_MOTHER'
  if (hasFather) return 'FAMILY_DUO_FATHER'
  return 'PROBAND_ONLY'
}

function mapPhysician(prov: PhysicianInfo | undefined): CanonicalMedicalProvider | null {
  if (!prov) return null
  return {
    id: prov.id ?? null,
    npi: prov.npi ?? null,
    name: prov.name ? { firstName: prov.name.firstName ?? null, lastName: prov.name.lastName ?? null } : null,
    email: prov.email ?? null,
    phone: prov.phone ?? null,
    roleTitle: prov.roleTitle ?? null,
    credentials: prov.credentials ?? null,
  }
}

function mapProfessionalInfo(info: AuthorizingProfessionalInfo | undefined): CanonicalMedicalProfessionalInfo | null {
  if (!info) return null
  return {
    npi: info.npi ?? null,
    name: info.name ?? null,
    email: info.email ?? null,
  }
}

function mapParent(parent: ParentFieldShape | undefined): CanonicalParentInfo | null {
  if (!parent) return null
  return {
    parentType: parent.parentType ?? null,
    parentFirstName: parent.parentFirstName ?? null,
    parentLastName: parent.parentLastName ?? null,
    parentDateOfBirth: parent.parentDateOfBirth ?? null,
    parentSex: parent.parentSex ?? null,
    parentMRN: parent.parentMRN ?? null,
    parentEmail: parent.parentEmail ?? null,
    parentPhone: parent.parentPhone ?? null,
    collectionType: parent.collectionType ?? null,
    specimenId: parent.specimenId ?? null,
    demographicId: parent.demographicId ?? null,
    phenotypicTrait: parent.phenotypicTrait ?? null,
    icd10Terms: parent.icd10Terms ?? null,
    hpoTerms: parent.hpoTerms ?? [],
    clinicalNote:
      typeof parent.clinicalNote === 'string' && parent.clinicalNote.trim() ? parent.clinicalNote.trim() : null,
    isPrimaryGuardian: parent.isPrimaryGuardian === true ? true : parent.isPrimaryGuardian === false ? false : null,
    includeIncidentalFindings:
      parent.includeIncidentalFindings === true ? true : parent.includeIncidentalFindings === false ? false : null,
    newYorkSampleRetentionOptIn: parent.newYorkSampleRetentionOptIn === true,
    researchContactConsent: parent.researchContactConsent === true,
  }
}

// ─── Public mappers ───────────────────────────────────────────────────────────

/**
 * Build CanonicalEhrContext from an Epic SMART state key plus an optional
 * encounter ID selected at submit time.
 */
export function mapEpicContextToEhrContext(epicState: string, ehrEncounterId?: string | null): CanonicalEhrContext {
  const ctx = EpicAuthClient.getInstance().getContext(epicState)
  return {
    fhirPatientId: ctx.fhirPatientId ?? null,
    fhirUserId: ctx.fhirUserId ?? null,
    mrnIdentifierSystem: ctx.mrnIdentifierSystem ?? null,
    ehrEncounterId: ehrEncounterId ?? null,
  }
}

/** Coerce IMDB/JSON quirks (snake_case keys) before mapping. */
function normalizeImdbRequisitionPartial(
  rfd: PartialRequisitionFormData | null | undefined
): PartialRequisitionFormData {
  if (!rfd || typeof rfd !== 'object') return {}
  const o = rfd as Record<string, unknown>
  const next: PartialRequisitionFormData = { ...rfd }
  if (!next.guardianContact && o['guardian_contact'] && typeof o['guardian_contact'] === 'object') {
    next.guardianContact = o['guardian_contact'] as PartialRequisitionFormData['guardianContact']
  }
  if (
    !next.primaryConsentFamilyMember &&
    o['primary_consent_family_member'] &&
    typeof o['primary_consent_family_member'] === 'object'
  ) {
    next.primaryConsentFamilyMember = o[
      'primary_consent_family_member'
    ] as PartialRequisitionFormData['primaryConsentFamilyMember']
  }
  return next
}

/** Map IMDB familyMember.relationship (e.g. bio_mother, legal_guardian) back to guardian UI enum. */
function familyMemberRelToGuardianUi(rel: string | null | undefined): string | null {
  const raw = (rel ?? '').trim()
  const u = raw.toLowerCase()
  if (u === 'bio_mother') return 'MOTHER'
  if (u === 'bio_father') return 'FATHER'
  // family_member.relationship is varchar on IMDB (the legacy enum was dropped),
  // so 'legal_guardian' round-trips verbatim once guardianRelationshipToFamilyMember
  // stops collapsing it to 'other' (GEN-2613).
  if (u === 'legal_guardian') return 'LEGAL_GUARDIAN'
  // Extended IMDB lineage values (carebus-client 1.43.0) are emitted verbatim as
  // UPPER_CASE, so round-trip them straight back to the guardian UI enum.
  const upper = raw.toUpperCase()
  if (
    upper !== 'MOTHER' &&
    upper !== 'FATHER' &&
    upper !== 'LEGAL_GUARDIAN' &&
    upper !== 'OTHER' &&
    (GUARDIAN_RELATIONSHIP_VALUES as readonly string[]).includes(upper)
  ) {
    return upper
  }
  // 'other' is intentionally NOT coerced back to 'OTHER'. Legacy submitted
  // orders (pre-GEN-2613) collapsed both OTHER and LEGAL_GUARDIAN to 'other'
  // at submit, so on reload we cannot tell which was originally picked. The
  // merge in augmentCanonicalWithRfdGuardian preserves any existing canonical
  // guardianContact.relationshipToPatient (which carries the original UI value
  // verbatim); if that has been wiped server-side, the user re-picks the
  // relationship in the modal rather than being shown a silently wrong value.
  return null
}

function splitSignerDisplayNameToFirstLast(s: string | null | undefined): {
  firstName: string | null
  lastName: string | null
} {
  const t = (s ?? '').trim()
  if (!t) return { firstName: null, lastName: null }
  const i = t.indexOf(' ')
  if (i === -1) return { firstName: t, lastName: null }
  return {
    firstName: t.slice(0, i).trim() || null,
    lastName: t.slice(i + 1).trim() || null,
  }
}

function mapPcmRfdToGuardianCanonical(
  pcm: PartialRequisitionFormData['primaryConsentFamilyMember'] | null | undefined
): CanonicalGuardianContact | null {
  if (!pcm || typeof pcm !== 'object') return null
  if (pcm.contactNotAvailable === true) {
    return {
      contactNotAvailable: true,
      firstName: null,
      lastName: null,
      dateOfBirth: null,
      email: null,
      phone: null,
      relationshipToPatient: null,
    }
  }
  const relUi = familyMemberRelToGuardianUi(pcm.relationship)
  let firstName = pcm.firstName != null && String(pcm.firstName).trim() !== '' ? pcm.firstName : null
  let lastName = pcm.lastName != null && String(pcm.lastName).trim() !== '' ? pcm.lastName : null
  if (!firstName && !lastName && pcm.signerDisplayName?.trim()) {
    const sp = splitSignerDisplayNameToFirstLast(pcm.signerDisplayName)
    firstName = sp.firstName
    lastName = sp.lastName
  }
  const hasEmailOrPhone =
    (pcm.email != null && String(pcm.email).trim() !== '') || (pcm.phone != null && String(pcm.phone).trim() !== '')
  const dob = pcm.dateOfBirth != null && String(pcm.dateOfBirth).trim() !== '' ? pcm.dateOfBirth : null
  const hasAny = Boolean(firstName) || Boolean(lastName) || Boolean(dob) || hasEmailOrPhone || relUi != null
  if (!hasAny) return null
  return {
    contactNotAvailable: pcm.contactNotAvailable ?? null,
    firstName,
    lastName,
    dateOfBirth: dob,
    email: pcm.email ?? null,
    phone: pcm.phone ?? null,
    relationshipToPatient: relUi,
  }
}

function canonicalGuardianHasDisplayData(g: CanonicalGuardianContact | null | undefined): boolean {
  if (!g) return false
  if (g.contactNotAvailable === true) return true
  return [g.firstName, g.lastName, g.dateOfBirth, g.email, g.phone, g.relationshipToPatient].some(
    (v) => v != null && String(v).trim() !== ''
  )
}

/** True when canonical already has enough guardian contact that we should not overlay RFD / CareBus needs contact details with research opt-in. */
export function guardianContactHasSubstantiveContact(g: CanonicalGuardianContact | null | undefined): boolean {
  if (!g) return false
  if (g.contactNotAvailable === true) return true
  return [g.firstName, g.lastName, g.email, g.phone].some((v) => v != null && String(v).trim() !== '')
}

/**
 * Whether a secondary-consent boolean is a PROVIDER choice vs. the enforced
 * hidden-slot default (`useEnforceNonApplicableSecondaryConsentDefaults` only ever
 * writes `false`, only on a non-applicable slot). A `true` is always a provider
 * opt-in; a `false` counts only when the slot is applicable.
 */
function consentValueIsProviderAuthored(value: boolean, applicable: boolean | undefined): boolean {
  if (value === true) return true
  return applicable === true
}

/**
 * Whether a comparator row carries PROVIDER-authored content vs. being a bare
 * system-seeded parent row. On a trio/duo default test type,
 * `initCanonicalFromOrderFormInfo` seeds relationship-only MOTHER/FATHER rows
 * (`relationship`, `isPrimaryGuardian`, and a `null` incidental sentinel) with ZERO
 * provider interaction — so a blanket "any comparator = content" check lets an
 * untouched trio launch seed defeat the deferred-create backstop and fire a
 * bare-seed POST. A row counts as authored when it carries demographics, specimen,
 * phenotype/HPO/ICD10/clinical, a sequencing/kit/multiple-birth choice, a genuine
 * consent choice (incidental, a `true` NY/research opt-in, OR a NY/research value
 * on an APPLICABLE slot — an applicable-slot `false` is a provider opt-OUT, not the
 * enforced hidden-slot default), or is a NON-parent relationship (siblings /
 * grandparents / etc. are only added through the comparator UI after the session
 * exists, so their mere presence is provider intent).
 *
 * `consentApplicability` mirrors the proband nuance: parents share the same
 * NY/research slot applicability as the proband (one `consents` config drives both),
 * so an enforced hidden-slot `false` is excluded while an applicable-slot choice counts.
 */
function comparatorHasProviderAuthoredContent(
  c: Comparator | null | undefined,
  consentApplicability?: { ny?: boolean; research?: boolean }
): boolean {
  if (!c) return false
  // Non-parent relationships are never system-seeded at launch → provider intent.
  if (c.relationship !== 'MOTHER' && c.relationship !== 'FATHER' && c.relationship !== 'PROBAND') {
    return true
  }

  const d = c.demographic
  const demographicHasContent =
    d != null &&
    [d.firstName, d.lastName, d.middleName, d.mrn, d.email, d.phone, d.dateOfBirth, d.sex].some(
      (v) => v != null && String(v).trim() !== ''
    )
  if (demographicHasContent) return true

  const specimen = c.specimen
  if (specimen != null && (specimen.specimenId != null || specimen.collectionType != null)) return true

  if (
    (c.phenotypicTrait != null && String(c.phenotypicTrait).trim() !== '') ||
    (c.icd10Terms != null && String(c.icd10Terms).trim() !== '') ||
    (c.clinicalNote != null && String(c.clinicalNote).trim() !== '') ||
    (Array.isArray(c.hpoTerms) && c.hpoTerms.length > 0)
  ) {
    return true
  }

  // Provider-authored row-level choices never present on a bare launch seed:
  // whether the parent is sequenced, a kit-shipment request, or multiple-birth data.
  if (c.isTestingComparator === true || c.isTestingComparator === false) return true
  if (c.kitShipmentRequested != null) return true
  if (c.multipleBirth != null) return true

  const sc = c.secondaryConsent
  if (sc != null) {
    // Incidental: `null` is the untouched sentinel; any explicit choice is intent.
    if (sc.includeIncidentalFindings === true || sc.includeIncidentalFindings === false) return true
    // NY/research: a `true` opt-in always counts; a `false` counts only on an
    // APPLICABLE slot (an enforced hidden-slot default is only ever `false`).
    const ny = sc.newYorkSampleRetentionOptIn
    if ((ny === true || ny === false) && consentValueIsProviderAuthored(ny, consentApplicability?.ny)) return true
    const research = sc.researchContactConsent
    if (
      (research === true || research === false) &&
      consentValueIsProviderAuthored(research, consentApplicability?.research)
    ) {
      return true
    }
  }

  return false
}

/**
 * Single source of truth for "the provider has authored order content" (GEN-5383
 * arch-review D-SF4). Both the deferred-create seed guard (`isUntouchedLaunchSeed`,
 * which negates this) and the resume-vs-defer branch (`hasDraftContent`, D5) consume
 * this so "touched"/"has content" cannot drift from each other or from the submit
 * gate (`FormCompletionChecker.isCanonicalOrderSubmittable`).
 *
 * Returns `true` for any provider-authored content: selected test type (see D1
 * nuance), clinical note/differential/prior genetic testing, reason interaction /
 * clinicalDiagnosis / icd10Diagnosis (string OR array), a comparator with
 * provider-authored content (NOT a bare system-seeded trio/duo parent row), family
 * content, a provider consent choice (see D-1 nuance), or a provider-authored
 * guardian (only when `guardianUserTouched`). System-derived fields (demographics,
 * collectionType, ordering site, lab name, ehrContext, order type) never count.
 *
 * D1 (`selectedOptionIsUntouchedConfigDefault`): on default-test-type orgs the
 * deferred seed carries a non-null `selectedOption` the provider never picked; when
 * the flag is set it does NOT count. Standalone/resume leave it `false`.
 *
 * D-1 consent nuance: `useEnforceNonApplicableSecondaryConsentDefaults` writes
 * NY/research `false` on mount for non-applicable slots (a system default). Counting
 * that reintroduced the bare-seed POST (I1), so NY/research count only when the slot
 * is applicable or the value is a `true` opt-in. Incidental is never auto-seeded
 * (`null` = untouched), so a non-null incidental choice always counts (I2).
 */
export function canonicalHasProviderAuthoredContent(
  canonical: CanonicalOrderPayload | null | undefined,
  opts?: {
    guardianUserTouched?: boolean
    selectedOptionIsUntouchedConfigDefault?: boolean
    /**
     * Whether each secondary-consent slot is applicable (card shown). Applicable →
     * any boolean counts; not applicable → a `false` is the enforced default and is
     * ignored. Omit for the conservative fallback (count only `true` opt-ins).
     */
    consentApplicability?: {
      ny?: boolean
      research?: boolean
    }
  }
): boolean {
  if (!canonical) return false
  const guardianUserTouched = opts?.guardianUserTouched === true
  const selectedOptionIsUntouchedConfigDefault = opts?.selectedOptionIsUntouchedConfigDefault === true
  const nyApplicable = opts?.consentApplicability?.ny
  const researchApplicable = opts?.consentApplicability?.research

  // Test selection. A config-default `selectedOption` the provider never picked
  // (D1) is NOT authored content on the deferred path.
  if (canonical.order?.selectedOption != null && !selectedOptionIsUntouchedConfigDefault) {
    return true
  }

  const clinical = canonical.clinical
  const clinicalHasContent =
    (clinical?.clinicalNote != null && String(clinical.clinicalNote).trim() !== '') ||
    (clinical?.differentialDiagnosis != null && String(clinical.differentialDiagnosis).trim() !== '') ||
    (clinical?.priorGeneticTesting != null && String(clinical.priorGeneticTesting).trim() !== '')
  if (clinicalHasContent) return true

  const reason = canonical.reason
  if (reason?.userInteracted === true) return true
  if (Array.isArray(reason?.clinicalDiagnosis) && reason.clinicalDiagnosis.length > 0) return true
  // icd10Diagnosis runtime shape may be string OR array; handle both.
  const icd10 = reason?.icd10Diagnosis as unknown
  if (Array.isArray(icd10) && icd10.length > 0) return true
  if (typeof icd10 === 'string' && icd10.trim() !== '') return true

  if (
    Array.isArray(canonical.comparators) &&
    canonical.comparators.some((c) =>
      comparatorHasProviderAuthoredContent(c, { ny: nyApplicable, research: researchApplicable })
    )
  ) {
    return true
  }

  const family = canonical.family
  const familyHasContent =
    (family?.familyHistory != null && String(family.familyHistory).trim() !== '') ||
    family?.hasParent != null ||
    family?.hasGrandparent != null ||
    family?.hasSibling != null ||
    family?.hasCousin != null
  if (familyHasContent) return true

  // Provider consent choices (D3/D-1). See the doc comment: incidental always
  // counts; NY/research only when applicable or a `true` opt-in.
  const patient = canonical.patient
  if (patient?.incidentalFindingsSelection != null) return true
  const ny = patient?.newYorkSampleRetentionOptIn
  if ((ny === true || ny === false) && consentValueIsProviderAuthored(ny, nyApplicable)) {
    return true
  }
  const research = patient?.researchContactConsent
  if ((research === true || research === false) && consentValueIsProviderAuthored(research, researchApplicable)) {
    return true
  }

  // A guardian only counts as content once the provider has TOUCHED the section.
  // An untouched Epic-prefill guardian (populated by the FHIR load, never edited)
  // is not provider intent to order — mirror guardianUserTouchedRef semantics.
  if (guardianUserTouched && guardianContactHasSubstantiveContact(canonical.guardianContact)) {
    return true
  }

  return false
}

/** True when guardianContact holds at least one populated contact field (name/email/phone). */
function canonicalGuardianHasRealContact(g: CanonicalGuardianContact | null | undefined): boolean {
  if (!g) return false
  return [g.firstName, g.lastName, g.email, g.phone].some((v) => v != null && String(v).trim() !== '')
}

function preferNonBlankGuardianField(a: string | null | undefined, b: string | null | undefined): string | null {
  if (a != null && String(a).trim() !== '') return a
  if (b != null && String(b).trim() !== '') return b
  return null
}

/**
 * When `json_payload.canonicalOrder` exists but omits guardian, fill from RFD
 * (`guardianContact` or `primaryConsentFamilyMember` after submit merge).
 */
export function augmentCanonicalWithRfdGuardian(
  canonical: CanonicalOrderPayload,
  rfd: PartialRequisitionFormData | null | undefined
): CanonicalOrderPayload {
  const rfdNorm = normalizeImdbRequisitionPartial(rfd ?? undefined)
  // Guardian source priority: RFD's guardianContact, RFD's
  // primaryConsentFamilyMember, then canonical's own primaryConsent.familyMember.
  // The last fallback is required for proband-only orders submitted via the
  // canonical session flow: the server persists guardian-derived data into
  // primary_consent (so it survives in canonicalOrder.primaryConsent.familyMember
  // on reload) but does not always echo it back as canonicalOrder.guardianContact.
  // Without this fallback, the Edit Guardian Contact modal opens with empty
  // fields on a reloaded submitted order.
  const fromRfd =
    mapRfdGuardianToCanonical(rfdNorm.guardianContact) ??
    mapPcmRfdToGuardianCanonical(rfdNorm.primaryConsentFamilyMember) ??
    mapPcmRfdToGuardianCanonical(
      canonical.primaryConsent?.familyMember as
        PartialRequisitionFormData['primaryConsentFamilyMember'] | null | undefined
    )
  if (!fromRfd) return canonical
  // We intentionally always run the field-by-field merge below (no early
  // return when guardianContact is "substantive"). The server may return a
  // partially populated guardianContact from primary_consent backfill —
  // firstName, lastName, email, phone — but omit dateOfBirth or
  // relationshipToPatient. The merge uses `preferNonBlankGuardianField`,
  // which keeps any existing canonical value and only fills BLANKS from the
  // fallback source, so it is purely additive for those fields.
  //
  // The `contactNotAvailable` flag needs special care: a stale RFD may
  // carry `contactNotAvailable: true` even when canonical already holds real
  // contact data (name / email / phone). We must not flip the flag in that
  // case, otherwise reloading wipes the guardian record. Only let
  // fromRfd.contactNotAvailable promote to `true` when canonical has no real
  // contact data of its own.
  const cur = canonical.guardianContact
  const curHasRealContact = canonicalGuardianHasRealContact(cur)
  const mergedContactNotAvailable =
    cur?.contactNotAvailable === true
      ? true
      : curHasRealContact
        ? (cur?.contactNotAvailable ?? null)
        : (fromRfd.contactNotAvailable ?? null)
  const merged: CanonicalGuardianContact = {
    contactNotAvailable: mergedContactNotAvailable,
    firstName: preferNonBlankGuardianField(cur?.firstName, fromRfd.firstName),
    lastName: preferNonBlankGuardianField(cur?.lastName, fromRfd.lastName),
    dateOfBirth: preferNonBlankGuardianField(cur?.dateOfBirth, fromRfd.dateOfBirth),
    email: preferNonBlankGuardianField(cur?.email, fromRfd.email),
    phone: preferNonBlankGuardianField(cur?.phone, fromRfd.phone),
    relationshipToPatient: preferNonBlankGuardianField(cur?.relationshipToPatient, fromRfd.relationshipToPatient),
  }
  if (!canonicalGuardianHasDisplayData(merged)) return canonical
  return { ...canonical, guardianContact: merged }
}

/**
 * When GET returns a small `canonicalOrder` (e.g. only proband secondary-consent fields merged on
 * the server) but `requisitionFormData` is still the full TRF, rebuild a display-ready canonical from
 * TRF then overlay the sparse server payload so consents and other patches win without dropping
 * `orderType`, `order`, `comparators`, or patient demographics.
 *
 * GEN-3393: parent state lives on `comparators[]`. When the sparse server patch carries a
 * `comparators[]` array it wins wholesale (it is the authoritative server projection, including
 * merged secondary consents); otherwise the TRF-derived `legacy.comparators` is preserved.
 */
export function rehydrateSparseCanonicalWithLegacyTrf(
  sparse: CanonicalOrderPayload,
  legacy: CanonicalOrderPayload
): CanonicalOrderPayload {
  const { patient: pSp, orderType: _sparseOrderType, order: orderSp, comparators: comparatorsSp, ...rest } = sparse
  return {
    ...legacy,
    ...rest,
    /** Family structure is derived from TRF; a sparse server patch (often missing orderType) must not be overwritten with PROBAND_ONLY. */
    orderType: legacy.orderType,
    patient: pSp != null ? { ...(legacy.patient ?? {}), ...pSp } : legacy.patient,
    /**
     * The persisted sparse canonical's `order` block can carry a null
     * `selectedOption` (and other null `order` fields) even when the legacy TRF
     * knows the real test identity. A wholesale `...rest` spread lets that null
     * clobber the good legacy value, which zeroes out `selectedTestTypeConfig`
     * and disables comparator editing on reload. Merge the `order` block
     * field-by-field instead, preferring the sparse value but falling back to
     * the legacy TRF value whenever the sparse field is null / undefined / blank.
     */
    order: mergeOrderPreferNonBlank(orderSp, legacy.order),
    /**
     * The sparse server projection typically carries only the parent comparators
     * (with merged secondary consents); a wholesale replace would drop non-parent
     * comparators (siblings, guardians, extended family) that only exist on the
     * TRF-derived legacy list — making them vanish from the R5 side-nav on reload.
     * Merge by identity instead: prefer the sparse row where the server has one,
     * but preserve any legacy-only rows the sparse patch omitted.
     */
    comparators: mergeComparatorsPreservingLegacy(comparatorsSp, legacy.comparators),
  }
}

/** Trimmed non-empty client id, or null. */
function comparatorId(c: Comparator): string | null {
  return typeof c.id === 'string' && c.id.trim() !== '' ? c.id.trim() : null
}

function comparatorDemographicId(c: Comparator): string | null {
  return typeof c.demographic?.demographicId === 'string' && c.demographic.demographicId.trim() !== ''
    ? c.demographic.demographicId.trim()
    : null
}

/** Normalize a demographic string field for identity comparison (trim + lowercase). */
function normalizeDemographicField(v: string | null | undefined): string {
  return typeof v === 'string' ? v.trim().toLowerCase() : ''
}

/**
 * A stable "same person" fingerprint for a NON-parent comparator — relationship
 * + normalized first/last name + DOB. Returns ``null`` unless the row carries a
 * relationship and at least a name (first or last) OR a DOB, so blank rows never
 * collide.
 *
 * Used by ``comparatorsRefersToSameEntity`` to pair the two projections of a
 * manually-added family member across a reload when they share no id /
 * demographicId signal — e.g. the persisted ``canonicalOrder`` row (backfilled
 * client id, no ``demographicId``) and the ``requisitionFormData`` projection
 * (``id: null``, with or without a server ``demographicId``). The caller
 * suppresses this only when BOTH rows already carry a demographicId.
 */
function comparatorDemographicIdentityKey(c: Comparator): string | null {
  const rel = normalizeDemographicField(c.relationship as string | null | undefined)
  const first = normalizeDemographicField(c.demographic?.firstName)
  const last = normalizeDemographicField(c.demographic?.lastName)
  const dob = normalizeDemographicField(c.demographic?.dateOfBirth)
  if (!rel) return null
  if (first === '' && last === '' && dob === '') return null
  return `${rel}|${first}|${last}|${dob}`
}

/**
 * Do two comparator rows refer to the SAME family member for hydration merges?
 *
 * A single comparator can be projected differently by each source: after a
 * reload the persisted ``canonicalOrder`` row typically carries a backfilled
 * client ``id`` (but no ``demographicId``), while the ``requisitionFormData``
 * projection of the same person carries ``id: null`` plus a server
 * ``demographicId``. A single precedence-ordered key (id → demographicId) would
 * therefore emit ``id:…`` for one and ``demo:…`` for the other and never pair
 * them — duplicating siblings / aunts / guardians in the side-nav and PUTs.
 *
 * So, mirroring the write-side ``findExistingComparatorIndex``, we test EACH
 * identity signal independently and match on ANY agreement:
 *   1. parent **side** (MOTHER / FATHER are 1-of-1, authoritative);
 *   2. client ``id``;
 *   3. ``demographic.demographicId``;
 *   4. demographic fingerprint — relationship + name + DOB — for non-parent rows
 *      that do NOT both carry a demographicId (a manually-added sibling / aunt
 *      whose two projections share only name + DOB, including the asymmetric
 *      reload case where one side has a server demographicId and the other has a
 *      backfilled id). Suppressed only when BOTH rows carry a demographicId,
 *      since two present-but-unequal ids are authoritative evidence of distinct
 *      people. Two genuinely distinct siblings differ in name/DOB, so this never
 *      collapses real multiples.
 *
 * Rows that share none of those signals (e.g. two blank ``id: null`` rows with no
 * name or DOB) are treated as DISTINCT so repeatable relationships never
 * collapse.
 */
function comparatorsRefersToSameEntity(a: Comparator, b: Comparator): boolean {
  const sideA = parentSideForComparator(a)
  const sideB = parentSideForComparator(b)
  if (sideA != null || sideB != null) return sideA != null && sideA === sideB

  const idA = comparatorId(a)
  if (idA != null && idA === comparatorId(b)) return true

  const demoA = comparatorDemographicId(a)
  const demoB = comparatorDemographicId(b)
  if (demoA != null && demoA === demoB) return true

  // Fingerprint (relationship + name + DOB) pairs the same person's two
  // projections when at least one side lacks a demographicId — including the
  // asymmetric reload case: a persisted row with a backfilled client id and NO
  // demographicId vs the RFD projection with id: null plus a server
  // demographicId. That pair shares no id/demographicId signal, so without the
  // fingerprint the same person is appended again on every reload.
  //
  // It is suppressed ONLY when BOTH rows carry a demographicId: since equal ids
  // already returned true above, two present-but-unequal demographicIds are
  // authoritative evidence of DISTINCT people (e.g. same-named twins), and the
  // name/DOB fingerprint must not override them.
  const bothHaveDemographicId = demoA != null && demoB != null
  if (!bothHaveDemographicId) {
    const fpA = comparatorDemographicIdentityKey(a)
    if (fpA != null && fpA === comparatorDemographicIdentityKey(b)) return true
  }

  return false
}

/**
 * Index of the first row in ``pool`` that refers to the same entity as
 * ``target`` (see ``comparatorsRefersToSameEntity``) and is not already claimed
 * in ``claimed``. Returns -1 when there is no unclaimed match — a row with no
 * stable identity therefore always reports "no match" (append as distinct).
 */
function findComparatorMatchIndex(
  pool: ReadonlyArray<Comparator>,
  target: Comparator,
  claimed: ReadonlySet<number>
): number {
  for (let i = 0; i < pool.length; i++) {
    if (claimed.has(i)) continue
    if (comparatorsRefersToSameEntity(pool[i], target)) return i
  }
  return -1
}

/**
 * Merge a sparse server comparators array over the legacy-TRF-derived one,
 * preserving legacy-only rows the sparse patch omitted (see
 * ``rehydrateSparseCanonicalWithLegacyTrf``).
 *
 * - When the sparse array is null/undefined, keep the legacy list untouched.
 * - When both carry a row for the same entity (``comparatorsRefersToSameEntity``
 *   — matching on parent side / id / demographicId, tried independently so a
 *   backfilled-id row still pairs with an id:null + demographicId projection),
 *   the sparse row wins (authoritative server projection / merged consents), in
 *   the legacy list's order.
 * - Legacy-only rows are retained (in legacy order); sparse-only rows are
 *   appended after. Each sparse row is consumed by at most one legacy row.
 * - Rows with no shared identity signal (a non-parent row lacking id,
 *   demographicId, and a name/DOB fingerprint) never match, so repeatable
 *   relationships (e.g. two siblings) don't collapse.
 */
function mergeComparatorsPreservingLegacy(
  sparse: Comparator[] | null | undefined,
  legacy: Comparator[] | null | undefined
): Comparator[] | null {
  const legacyRows = Array.isArray(legacy) ? legacy.filter((c): c is Comparator => c != null) : []
  // No sparse projection to overlay → the legacy list is authoritative as-is
  // (preserve the original reference/null when there were no legacy rows).
  if (!Array.isArray(sparse)) return legacyRows.length > 0 ? legacyRows : (legacy ?? null)
  // Drop guardian-only rows (legal guardian) — a guardian is captured on the
  // dedicated `guardianContact` field, never as a comparator (see
  // `isGuardianOnlyComparatorRelationship`).
  const sparseRows = sparse.filter(
    (c): c is Comparator => c != null && !isGuardianOnlyComparatorRelationship(c.relationship)
  )

  const claimedSparse = new Set<number>()
  const merged: Comparator[] = legacyRows.map((legacyRow) => {
    const idx = findComparatorMatchIndex(sparseRows, legacyRow, claimedSparse)
    if (idx >= 0) {
      claimedSparse.add(idx)
      return sparseRows[idx]
    }
    return legacyRow
  })
  // Append sparse-only rows the legacy list did not know about (and every
  // no-identity row, which never matches).
  sparseRows.forEach((c, i) => {
    if (!claimedSparse.has(i)) merged.push(c)
  })
  return merged.length > 0 ? merged : null
}

/**
 * Overlay the authoritative full comparator list from ``requisitionFormData.comparators``
 * onto a reconstructed canonical.
 *
 * On reload, canonical-first orders may carry a SPARSE ``canonicalOrder`` (only
 * proband consents + ehrContext, no ``comparators``), and the legacy-TRF
 * reconstruction only seeds MOTHER / FATHER from the parent slots — so non-parent
 * comparators (siblings, aunts/uncles, guardians) that the server DID persist in
 * ``requisitionFormData.comparators`` were dropped from the R5 side-nav.
 *
 * The RFD comparators are the complete server projection of WHO is on the order
 * (all relationships, demographics/specimen-rich), but RFD is documented as
 * demographics/specimen-rich — its per-row ``secondaryConsent`` / consent flags
 * can be sparser than the ``canonicalOrder`` projection, which
 * ``rehydrateSparseCanonicalWithLegacyTrf`` already merged onto the matched
 * parents. So for a matched row we MERGE (RFD demographics/specimen win; the
 * existing row's consent-bearing blocks are preserved unless RFD carries an
 * explicit value) rather than replacing wholesale — otherwise a reload would
 * wipe the server-merged sparse consents. Existing-only rows are preserved and
 * RFD-only rows appended. No-op when the RFD carries no comparators array.
 */
export function overlayRichComparatorsFromRfd(
  canonical: CanonicalOrderPayload,
  rfdComparators: ReadonlyArray<Comparator | null | undefined> | null | undefined
): CanonicalOrderPayload {
  if (!Array.isArray(rfdComparators)) return canonical
  // A legal guardian is not a sequencing comparator — it lives on the dedicated
  // `guardianContact` field. Drop any guardian-only row the server persisted into
  // `requisitionFormData.comparators[]` so it never rehydrates as a comparator
  // (which would surface it as a family-member/side-nav group after reload).
  const rfdRows = rfdComparators.filter(
    (c): c is Comparator => c != null && !isGuardianOnlyComparatorRelationship(c.relationship)
  )
  const existingRows = Array.isArray(canonical.comparators)
    ? canonical.comparators.filter((c): c is Comparator => c != null)
    : []

  const claimedRfd = new Set<number>()
  const merged: Comparator[] = existingRows.map((existing) => {
    const idx = findComparatorMatchIndex(rfdRows, existing, claimedRfd)
    // No match (incl. no-identity rows) → keep the existing row untouched.
    if (idx < 0) return existing
    claimedRfd.add(idx)
    return mergeRfdOntoExistingComparator(existing, rfdRows[idx])
  })
  // Append RFD-only rows (e.g. the non-parents the reconstruction dropped) and
  // every no-identity row, which never matches.
  rfdRows.forEach((c, i) => {
    if (!claimedRfd.has(i)) merged.push(c)
  })
  return { ...canonical, comparators: merged.length > 0 ? merged : null }
}

/** Prefer the RFD value only when it is a meaningful (non-null / non-undefined) value. */
function preferRfd<T>(rfdValue: T | null | undefined, existingValue: T | null | undefined): T | null | undefined {
  return rfdValue != null ? rfdValue : existingValue
}

/**
 * Merge an RFD comparator row onto the matched existing (post-sparse) row for the
 * ``overlayRichComparatorsFromRfd`` overlay. RFD's identity / demographics /
 * specimen / clinical fields win only when PRESENT: an explicit RFD ``null`` (the
 * projection is demographics/specimen-rich but can still omit fields) falls back
 * to the existing value via ``preferRfd``, so a reload never wipes a name / DOB /
 * specimen / HPO / ICD10 / clinical note the existing row holds. ``demographic``
 * is merged per sub-field for the same reason. The CONSENT-bearing state the
 * sparse ``canonicalOrder`` projection already merged (``secondaryConsent`` and
 * the ``isPrimaryGuardian`` / ``isTestingComparator`` / ``kitShipmentRequested``
 * flags) is likewise preserved unless the RFD carries an explicit value.
 */
function mergeRfdOntoExistingComparator(existing: Comparator, rfd: Comparator): Comparator {
  const secondaryConsent =
    rfd.secondaryConsent || existing.secondaryConsent
      ? {
          includeIncidentalFindings: preferRfd(
            rfd.secondaryConsent?.includeIncidentalFindings,
            existing.secondaryConsent?.includeIncidentalFindings
          ),
          newYorkSampleRetentionOptIn: preferRfd(
            rfd.secondaryConsent?.newYorkSampleRetentionOptIn,
            existing.secondaryConsent?.newYorkSampleRetentionOptIn
          ),
          researchContactConsent: preferRfd(
            rfd.secondaryConsent?.researchContactConsent,
            existing.secondaryConsent?.researchContactConsent
          ),
        }
      : existing.secondaryConsent
  // Demographics: RFD wins per-field when present, but an explicit null / omitted
  // sub-field falls back to the existing value so a sparse RFD demographic block
  // cannot blank a name / DOB / demographicId the existing row already holds.
  const demographic =
    rfd.demographic || existing.demographic
      ? {
          firstName: preferRfd(rfd.demographic?.firstName, existing.demographic?.firstName),
          lastName: preferRfd(rfd.demographic?.lastName, existing.demographic?.lastName),
          middleName: preferRfd(rfd.demographic?.middleName, existing.demographic?.middleName),
          dateOfBirth: preferRfd(rfd.demographic?.dateOfBirth, existing.demographic?.dateOfBirth),
          sex: preferRfd(rfd.demographic?.sex, existing.demographic?.sex),
          mrn: preferRfd(rfd.demographic?.mrn, existing.demographic?.mrn),
          email: preferRfd(rfd.demographic?.email, existing.demographic?.email),
          phone: preferRfd(rfd.demographic?.phone, existing.demographic?.phone),
          externalPatientId: preferRfd(rfd.demographic?.externalPatientId, existing.demographic?.externalPatientId),
          demographicId: preferRfd(rfd.demographic?.demographicId, existing.demographic?.demographicId),
        }
      : existing.demographic
  return {
    ...existing,
    ...rfd,
    // Keep the existing client id when RFD carries none (RFD rows arrive with
    // id: null); the id-backfill runs afterward regardless.
    id: preferRfd(rfd.id, existing.id),
    // Demographics / specimen / clinical: RFD wins only when present. An explicit
    // RFD null (sparse projection) must not wipe an existing value — the wholesale
    // `...rfd` spread above would otherwise clobber them.
    demographic,
    specimen: preferRfd(rfd.specimen, existing.specimen),
    hpoTerms: rfd.hpoTerms != null ? rfd.hpoTerms : existing.hpoTerms,
    icd10Terms: preferRfd(rfd.icd10Terms, existing.icd10Terms),
    clinicalNote: preferRfd(rfd.clinicalNote, existing.clinicalNote),
    phenotypicTrait: preferRfd(rfd.phenotypicTrait, existing.phenotypicTrait),
    multipleBirth: preferRfd(rfd.multipleBirth, existing.multipleBirth),
    // Consent-bearing state: preserve the sparse-merged values unless RFD is explicit.
    secondaryConsent,
    isPrimaryGuardian: preferRfd(rfd.isPrimaryGuardian, existing.isPrimaryGuardian),
    isTestingComparator: preferRfd(rfd.isTestingComparator, existing.isTestingComparator),
    kitShipmentRequested: preferRfd(rfd.kitShipmentRequested, existing.kitShipmentRequested),
  }
}

/**
 * Merge a sparse canonical `order` block over the legacy-TRF-derived one,
 * keeping the sparse (server/persisted) value for each field only when it is a
 * meaningful (non-null, non-blank) value; otherwise fall back to the legacy
 * value. This prevents a persisted `order.selectedOption: null` from erasing
 * the test identity the legacy TRF carries.
 */
function mergeOrderPreferNonBlank(
  sparse: CanonicalOrderSelection | null | undefined,
  legacy: CanonicalOrderSelection | null | undefined
): CanonicalOrderSelection {
  const base: CanonicalOrderSelection = { ...(legacy ?? {}) }
  if (sparse == null) return base
  const merged: CanonicalOrderSelection = { ...base }
  for (const [key, value] of Object.entries(sparse)) {
    const isBlank = value == null || (typeof value === 'string' && value.trim() === '')
    if (!isBlank) {
      ;(merged as Record<string, unknown>)[key] = value
    }
  }
  return merged
}

/**
 * Persisted `canonicalOrder.orderType` can be stale (e.g. FAMILY_DUO_FATHER) while the real intent is
 * proband-only + guardian parent. When legacy TRF mapping yields PROBAND_ONLY, downgrade orderType
 * so In Progress family cards match (never upgrade — avoids clobbering real trio).
 *
 * When `requisitionFormData` is absent (common on IMDB refresh), infer structure from canonical only
 * using the same guardian-path rules as `deriveFamilyStructureOrderTypeFromRfd`.
 */
export function reconcileCanonicalOrderTypeWithLegacyTrf(
  canonical: CanonicalOrderPayload,
  context: {
    orderFormInfo?: OrderFormInfoData | null
    requisitionFormData?: PartialRequisitionFormData | null
  }
): CanonicalOrderPayload {
  const ofi = context.orderFormInfo
  const rfd = context.requisitionFormData
  if (ofi && rfd != null) {
    const legacy = legacyTrfPayloadToCanonical({
      orderFormInfo: ofi,
      requisitionFormData: rfd,
    })
    if (legacy.orderType === 'PROBAND_ONLY' && canonical.orderType !== 'PROBAND_ONLY') {
      return { ...canonical, orderType: 'PROBAND_ONLY' }
    }
    return canonical
  }
  const inferred = deriveFamilyStructureOrderTypeFromCanonical(canonical)
  if (inferred === 'PROBAND_ONLY' && canonical.orderType !== 'PROBAND_ONLY') {
    return { ...canonical, orderType: 'PROBAND_ONLY' }
  }
  return canonical
}

/**
 * Map OrderFormInfoData provider slots to CanonicalProviders (same rules as TRF → canonical).
 */
export function canonicalProvidersFromOrderFormInfo(formInfo: OrderFormInfoData): CanonicalProviders {
  return {
    orderingProvider: mapPhysician(formInfo.orderingProvider),
    patientPhysician: mapPhysician(formInfo.patientPhysician),
    authorizingProvider: mapPhysician(formInfo.authorizingProvider),
    medicalProfessionalInfo: mapProfessionalInfo(formInfo.medicalProfessionalInfo),
    authorizingProfessionalInfo: mapProfessionalInfo(formInfo.authorizingProfessionalInfo),
  }
}

/**
 * Merge provider updates from the Ready-to-Send form into session canonical providers.
 * Incoming `null` slots are skipped so we do not wipe persisted session data with empty mappings.
 */
export function mergeCanonicalProviders(
  base: CanonicalProviders | null | undefined,
  incoming: CanonicalProviders | null | undefined
): CanonicalProviders | null {
  if (!base && !incoming) return null
  if (!incoming) return base ?? null
  if (!base) return incoming

  const out: CanonicalProviders = { ...base }
  if (incoming.orderingProvider != null) out.orderingProvider = incoming.orderingProvider
  if (incoming.patientPhysician != null) out.patientPhysician = incoming.patientPhysician
  if (incoming.authorizingProvider != null) out.authorizingProvider = incoming.authorizingProvider
  if (incoming.medicalProfessionalInfo != null) out.medicalProfessionalInfo = incoming.medicalProfessionalInfo
  if (incoming.authorizingProfessionalInfo != null) {
    out.authorizingProfessionalInfo = incoming.authorizingProfessionalInfo
  }
  return out
}

/**
 * Minimal provider shape needed to decide whether an order is a "submit on behalf of
 * another clinician" (delegate) order. Both `CanonicalMedicalProvider` and the TRF's
 * `PhysicianInfo` satisfy this shape, so the same comparison drives the PDF and the payload.
 */
export interface DelegateComparableProvider {
  npi?: string | null
  name?: { firstName?: string | null; lastName?: string | null } | null
  email?: string | null
}

function normalizeStr(v: string | null | undefined): string {
  return (v ?? '').trim()
}

/**
 * True when `orderingProvider` (the logged-in submitter) and `authorizingProvider`
 * refer to different people — i.e. the submitter is acting as a delegate on behalf of
 * another clinician.
 *
 * Comparison cascade (must stay in sync with the TRF renderer):
 *  1. Both NPIs present and differ  → on-behalf.
 *  2. Both emails present and differ → on-behalf.
 *  3. Names (case-insensitive first + last) differ → on-behalf.
 *  4. Otherwise → self-submit.
 */
export function isOnBehalfSubmit(
  orderingProvider: DelegateComparableProvider | null | undefined,
  authorizingProvider: DelegateComparableProvider | null | undefined
): boolean {
  if (!orderingProvider || !authorizingProvider) return false

  const onpi = normalizeStr(orderingProvider.npi)
  const anpi = normalizeStr(authorizingProvider.npi)
  if (onpi && anpi && onpi !== anpi) return true

  const oe = normalizeStr(orderingProvider.email).toLowerCase()
  const ae = normalizeStr(authorizingProvider.email).toLowerCase()
  if (oe && ae && oe !== ae) return true

  const ofn = normalizeStr(orderingProvider.name?.firstName).toLowerCase()
  const oln = normalizeStr(orderingProvider.name?.lastName).toLowerCase()
  const afn = normalizeStr(authorizingProvider.name?.firstName).toLowerCase()
  const aln = normalizeStr(authorizingProvider.name?.lastName).toLowerCase()
  const sameName = ofn === afn && oln === aln
  if (!sameName) return true

  return false
}

/**
 * Resolve the delegate (logged-in submitter acting on behalf of another clinician) from
 * canonical providers. Returns the `orderingProvider` when it differs from the
 * `authorizingProvider`, else `null` (self-submit, no delegate).
 *
 * The delegate is derived from the ordering vs. authorizing provider split, matching the
 * TRF renderer and provider-api exactly. Provider-api performs the authoritative delegate
 * stamping on the published payload; this helper is retained for UI-side parity/inspection.
 */
export function resolveDelegateFromCanonicalProviders(
  providers: CanonicalProviders | null | undefined
): CanonicalMedicalProvider | null {
  if (!providers) return null
  if (!isOnBehalfSubmit(providers.orderingProvider, providers.authorizingProvider)) return null
  return providers.orderingProvider ?? null
}

/**
 * Map the TRF form data to a CanonicalOrderPayload.
 * Private — only used by legacyTrfPayloadToCanonical.
 */
function mapRfdGuardianToCanonical(
  g: PartialRequisitionFormData['guardianContact'] | null | undefined
): CanonicalGuardianContact | null {
  if (!g || typeof g !== 'object') return null
  if (g.contactNotAvailable === true) {
    return {
      contactNotAvailable: true,
      firstName: null,
      lastName: null,
      dateOfBirth: null,
      email: null,
      phone: null,
      relationshipToPatient: null,
    }
  }
  const hasAny = [g.firstName, g.lastName, g.dateOfBirth, g.email, g.phone, g.relationshipToPatient].some(
    (v) => v != null && String(v).trim() !== ''
  )
  if (!hasAny) return null
  return {
    contactNotAvailable: g.contactNotAvailable ?? null,
    firstName: g.firstName ?? null,
    lastName: g.lastName ?? null,
    dateOfBirth: g.dateOfBirth ?? null,
    email: g.email ?? null,
    phone: g.phone ?? null,
    relationshipToPatient: g.relationshipToPatient ?? null,
  }
}

/**
 * True when an RFD parent row has real comparator / user-entered data.
 * `Boolean(reqFormData.mother)` is wrong for IMDB shells (`{}` is truthy).
 * Mirrors FormCompletionChecker.hasParentDataStarted; kept here to avoid importing
 * FormCompletionChecker (it depends on this module).
 */
function rfdParentHasComparatorData(data?: ParentFieldShape | null): boolean {
  if (!data) {
    return false
  }
  const hasTextData =
    (data.parentFirstName && data.parentFirstName.trim() !== '') ||
    (data.parentLastName && data.parentLastName.trim() !== '') ||
    (data.parentDateOfBirth && data.parentDateOfBirth.trim() !== '') ||
    (data.parentMRN && data.parentMRN.trim() !== '') ||
    (data.parentPhone && data.parentPhone.trim() !== '') ||
    (data.parentEmail && data.parentEmail.trim() !== '')
  const hasSelectedPhenotypicTrait = normalizePhenotypicTrait(data.phenotypicTrait) !== PhenotypicTrait.Unknown
  const hasSelectedCollectionType = data.collectionType && data.collectionType.trim() !== ''
  const hasHPOTerms = Array.isArray(data.hpoTerms) && data.hpoTerms.length > 0
  return Boolean(hasTextData || hasSelectedPhenotypicTrait || hasSelectedCollectionType || hasHPOTerms)
}

/** Family-structure orderType from TRF (legacy / IMDB), not raw object presence. */
function deriveFamilyStructureOrderTypeFromRfd(reqFormData: RequisitionFormData): string {
  let hasMother = rfdParentHasComparatorData(reqFormData.mother)
  let hasFather = rfdParentHasComparatorData(reqFormData.father)
  const gcRel = (reqFormData.guardianContact?.relationshipToPatient ?? '').toUpperCase()
  const pcmRel = (reqFormData.primaryConsentFamilyMember?.relationship ?? '').trim()
  const fatherGuardianPath = gcRel === 'FATHER' || pcmRel === 'bio_father'
  const motherGuardianPath = gcRel === 'MOTHER' || pcmRel === 'bio_mother'

  // Guardian stored on father/mother family_member (PM / IMDB): collection + demographics
  // are not a second biological comparator when the opposite parent has no comparator data.
  if (!hasMother && fatherGuardianPath) {
    hasFather = false
  }
  if (!hasFather && motherGuardianPath) {
    hasMother = false
  }

  return tierFromParentFlags(hasMother, hasFather)
}

/**
 * Infer family-structure orderType from canonical alone when `requisitionFormData` is missing on load.
 * Mirrors `deriveFamilyStructureOrderTypeFromRfd` guardian-path rules.
 */
function parentComparatorHasData(canonical: CanonicalOrderPayload, side: 'mother' | 'father'): boolean {
  const arr = canonical.comparators
  if (!Array.isArray(arr)) return false
  const literals = side === 'mother' ? ['MOTHER', 'bio_mother'] : ['FATHER', 'bio_father']
  const c = arr.find((row) => row && literals.includes(String(row.relationship).trim()))
  if (!c) return false
  const demo = c.demographic
  const hasTextData =
    (demo?.firstName && demo.firstName.trim() !== '') ||
    (demo?.lastName && demo.lastName.trim() !== '') ||
    (demo?.dateOfBirth && demo.dateOfBirth.trim() !== '') ||
    (demo?.mrn && demo.mrn.trim() !== '') ||
    (demo?.phone && demo.phone.trim() !== '') ||
    (demo?.email && demo.email.trim() !== '')
  const hasSelectedPhenotypicTrait =
    normalizePhenotypicTrait(c.phenotypicTrait ?? undefined) !== PhenotypicTrait.Unknown
  const hasSelectedCollectionType = Boolean(
    c.specimen?.collectionType && String(c.specimen.collectionType).trim() !== ''
  )
  const hasHPOTerms = Array.isArray(c.hpoTerms) && c.hpoTerms.length > 0
  return Boolean(hasTextData || hasSelectedPhenotypicTrait || hasSelectedCollectionType || hasHPOTerms)
}

function deriveFamilyStructureOrderTypeFromCanonical(canonical: CanonicalOrderPayload): string {
  // GEN-3393: parent presence is derived purely from `comparators[]`.
  let hasMother = parentComparatorHasData(canonical, 'mother')
  let hasFather = parentComparatorHasData(canonical, 'father')
  const gcRel = (canonical.guardianContact?.relationshipToPatient ?? '').toUpperCase()
  const pcmRel = (canonical.primaryConsent?.familyMember?.relationship ?? '').trim()
  const fatherGuardianPath = gcRel === 'FATHER' || pcmRel === 'bio_father'
  const motherGuardianPath = gcRel === 'MOTHER' || pcmRel === 'bio_mother'

  if (!hasMother && fatherGuardianPath) {
    hasFather = false
  }
  if (!hasFather && motherGuardianPath) {
    hasMother = false
  }

  return tierFromParentFlags(hasMother, hasFather)
}

function mapTrfToCanonical(
  formInfo: OrderFormInfoData,
  reqFormData: RequisitionFormData,
  ehrContext?: CanonicalEhrContext | null
): CanonicalOrderPayload {
  const p = formInfo.patient

  const patient: CanonicalPatient = {
    externalPatientId: p.id ?? null,
    mrn: reqFormData.patient.mrn || p.mrn || null,
    sex: reqFormData.patient.sex ?? null,
    firstName: reqFormData.patient.firstName || p.name?.firstName || null,
    lastName: reqFormData.patient.lastName || p.name?.lastName || null,
    middleName: reqFormData.patient.middleName || p.name?.middleName || null,
    dateOfBirth: reqFormData.patient.dateOfBirth ?? null,
    collectionType: reqFormData.patient.collectionType ?? null,
    specimenId: reqFormData.patient.specimenId ?? null,
    ethnicity: reqFormData.patient.ethnicity ?? p.ethnicity ?? null,
    race: reqFormData.patient.race ?? p.race ?? null,
  }

  const institution: CanonicalInstitution = {
    id: formInfo.institution?.id ?? formInfo.organizationId ?? null,
    name: formInfo.institution?.name ?? formInfo.organizationName ?? null,
  }

  const providers = canonicalProvidersFromOrderFormInfo(formInfo)

  const order: CanonicalOrderSelection = {
    selectedOption: reqFormData.order.selectedOption ?? null,
    labName: reqFormData.order.lab_name ?? null,
    // CMS CLIA for TRF is not on legacy order payload; seeded from org ui_config when building canonical.
    cliaLabId: null,
    encounterCsn: null,
  }

  const family: CanonicalFamily = {
    familyHistory: reqFormData.family.familyHistory ?? null,
    hasParent: reqFormData.family.hasParent ?? null,
    hasGrandparent: reqFormData.family.hasGrandparent ?? null,
    hasSibling: reqFormData.family.hasSibling ?? null,
    hasCousin: reqFormData.family.hasCousin ?? null,
  }

  const reason: CanonicalReason = {
    icd10Diagnosis: typeof reqFormData.reason.icd10Diagnosis === 'string' ? reqFormData.reason.icd10Diagnosis : null,
    userInteracted: reqFormData.reason.userInteracted ?? null,
    clinicalDiagnosis: reqFormData.reason.clinicalDiagnosis ?? [],
    // A submitted order's stored `canonicalOrder` is a server-synthesized stub, so the
    // real reason block arrives on the legacy requisition. This rebuild is field-by-field,
    // so the review has to be named here or the clinician's removals are dropped on the
    // way in and the order reopens as though nothing was ever taken off it.
    ...(reqFormData.reason.clinicalSummaryReview
      ? { clinicalSummaryReview: reqFormData.reason.clinicalSummaryReview }
      : {}),
  }

  const clinical: CanonicalClinical = {
    clinicalNote: reqFormData.clinical.clinicalNote ?? null,
    differentialDiagnosis: reqFormData.clinical.differentialDiagnosis?.trim()
      ? reqFormData.clinical.differentialDiagnosis.trim()
      : null,
    priorGeneticTesting: reqFormData.clinical.priorGeneticTesting?.trim()
      ? reqFormData.clinical.priorGeneticTesting.trim()
      : null,
  }

  // GEN-3393: build the legacy parent slots from the TRF then fold them into
  // `comparators[]` (stripping the slots) via the hydration-boundary normalizer.
  // The TRF (`requisitionFormData`) still carries `mother` / `father` — that is
  // GEN-3394 scope — so this is the one place the SPA reads the legacy parent
  // shape, and it converts straight to the comparators-only canonical shape.
  return normalizeInboundParentComparators({
    schemaVersion: CANONICAL_SCHEMA_VERSION,
    orderType: deriveFamilyStructureOrderTypeFromRfd(reqFormData),
    patient,
    institution,
    providers,
    order,
    family,
    mother: mapParent(reqFormData.mother),
    father: mapParent(reqFormData.father),
    reason,
    clinical,
    guardianContact:
      mapRfdGuardianToCanonical(reqFormData.guardianContact) ??
      mapPcmRfdToGuardianCanonical(reqFormData.primaryConsentFamilyMember),
    primaryConsent: null,
    ehrContext: ehrContext ?? null,
    version: reqFormData.version ?? 1,
  })
}

// ─── Reverse mappers (canonical → TRF shapes) ────────────────────────────────

function mapCanonicalProviderToPhysicianInfo(prov: CanonicalMedicalProvider): PhysicianInfo {
  return {
    id: prov.id ?? '',
    name: { firstName: prov.name?.firstName ?? '', lastName: prov.name?.lastName ?? '' },
    roleTitle: prov.roleTitle ?? '',
    phone: prov.phone ?? '',
    email: prov.email ?? '',
    npi: prov.npi ?? undefined,
    credentials: prov.credentials ?? undefined,
  }
}

/**
 * Reconstruct a partial OrderFormInfoData from a CanonicalOrderPayload.
 * Used to restore the page header (patient name, institution, providers) after a refresh
 * when location.state is no longer available.
 */
export function mapCanonicalToOrderFormInfo(canonical: CanonicalOrderPayload): Partial<OrderFormInfoData> {
  const p = canonical.patient
  const inst = canonical.institution
  const prov = canonical.providers

  return {
    ...(p && {
      patient: {
        id: p.externalPatientId ?? '',
        name: {
          firstName: p.firstName ?? '',
          lastName: p.lastName ?? '',
          middleName: p.middleName ?? undefined,
        },
        mrn: p.mrn ?? '',
        sex: (p.sex as Sex) ?? Sex.Unknown,
        birthDate: p.dateOfBirth ? (parseDateOnlyToLocalDate(p.dateOfBirth) ?? new Date(p.dateOfBirth)) : null,
      },
    }),
    ...(inst && {
      institution: {
        id: inst.id ?? '',
        name: inst.name ?? '',
        address: { street: '', city: '', state: '', zip: '', country: '' },
      },
    }),
    ...(inst?.id && { organizationId: inst.id }),
    ...(inst?.name && { organizationName: inst.name }),
    ...(prov?.orderingProvider && { orderingProvider: mapCanonicalProviderToPhysicianInfo(prov.orderingProvider) }),
    ...(prov?.patientPhysician && { patientPhysician: mapCanonicalProviderToPhysicianInfo(prov.patientPhysician) }),
    ...(prov?.authorizingProvider && {
      authorizingProvider: mapCanonicalProviderToPhysicianInfo(prov.authorizingProvider),
    }),
    ...(prov?.medicalProfessionalInfo && {
      medicalProfessionalInfo: {
        npi: prov.medicalProfessionalInfo.npi ?? '',
        name: prov.medicalProfessionalInfo.name ?? '',
        email: prov.medicalProfessionalInfo.email ?? undefined,
      },
    }),
  }
}

/**
 * Build a PrimaryConsent directly from authorizing provider details.
 * Use this on the OrderReadyToSend page for the session-based submit path,
 * bypassing the legacy ConsentFormData / buildConsentFormData → mapConsentToCanonical chain.
 */
export function buildCanonicalPrimaryConsent(opts: {
  name: string
  npi: string
  consentedText?: string | null
}): PrimaryConsent {
  const signatureDate = getLocalCalendarDateString()
  return {
    practitioner: {
      npi: opts.npi || null,
      name: opts.name || null,
      signature: opts.name || 'Signed electronically',
      signatureDate,
      consentedText: opts.consentedText || null,
    },
  }
}

/**
 * Minimal sampleType → CollectionType mapping (mirrors OrderStart local helper).
 * Exported so callers don't duplicate it.
 */
export function mapSampleTypeToCollectionType(
  sampleType: 'whole-blood' | 'buccal-swab' | 'saliva' | string | undefined
): string {
  if (sampleType === 'buccal-swab') return CollectionType.Buccal
  if (sampleType === 'whole-blood') return CollectionType.WholeBloodEDTA
  if (sampleType === 'saliva') return CollectionType.Saliva
  return CollectionType.Unknown
}

/**
 * Build an initial CanonicalOrderPayload from OrderFormInfoData + UI test options,
 * bypassing the legacy RequisitionFormData useMemo path.
 *
 * Use this when creating a new ordering session on page mount before the user has
 * filled in any form data. OrderStart dispatches fine-grained canonical actions as
 * the user makes edits; this call just seeds the initial session state.
 */
export function initCanonicalFromOrderFormInfo(
  formInfo: OrderFormInfoData,
  testOptions: {
    testType: string
    /**
     * Optional so callers (e.g. ``OrderStart`` initial state) can seed a session
     * before the user has chosen a sample type for product lines that allow
     * multiple proband sample types. Falsy values map to ``CollectionType.Unknown``
     * and the patient's ``collectionType`` is then persisted as ``null``.
     */
    sampleType?: 'whole-blood' | 'buccal-swab' | 'saliva' | string
    /**
     * P12 (MOTHER/FATHER migration): relationship-first selection from
     * `TestOptions.selectedComparatorRelationships`. Pass the raw list straight
     * through; only `'MOTHER'` and `'FATHER'` are honored when seeding the
     * initial parent comparators (other relationships are added through the
     * comparator UI after the session exists).
     */
    selectedComparatorRelationships?: readonly FamilyMemberRelationship[]
  },
  ehrContext?: CanonicalEhrContext | null,
  /** IMDB / session `lab_name` resolution (`getLabName`); TRF CLIA always comes from UiConfig at render time. */
  labName?: string
): CanonicalOrderPayload {
  const p = formInfo.patient
  const birthDate = p.birthDate ? (typeof p.birthDate === 'string' ? new Date(p.birthDate) : p.birthDate) : null

  const patient: CanonicalPatient = {
    externalPatientId: p.id ?? null,
    mrn: p.mrn ?? null,
    sex: (p.sex as string) ?? null,
    firstName: p.name?.firstName ?? null,
    lastName: p.name?.lastName ?? null,
    middleName: p.name?.middleName ?? null,
    dateOfBirth: birthDate ? birthDate.toISOString().split('T')[0] : null,
    collectionType: mapSampleTypeToCollectionType(testOptions.sampleType) || null,
    specimenId: null,
    ethnicity: p.ethnicity ?? null,
    race: p.race ?? null,
    // Incidental findings is never seeded — it requires an explicit provider
    // choice (opt-in / opt-out). `null` is the "unanswered" sentinel; the
    // submit gate blocks until the provider chooses when the section is
    // applicable AND authored `requireInteraction: true`.
    incidentalFindingsSelection: null,
  }

  const institution: CanonicalInstitution = {
    id: formInfo.institution?.id ?? formInfo.organizationId ?? null,
    name: formInfo.institution?.name ?? formInfo.organizationName ?? null,
  }

  const providers = canonicalProvidersFromOrderFormInfo(formInfo)

  // P12 (MOTHER/FATHER migration): derive the seeded parent comparators from
  // the relationship list directly. Only MOTHER / FATHER are honored here;
  // other relationships are added through the comparator UI after the session
  // exists.
  const seededRelationships = testOptions.selectedComparatorRelationships ?? []
  const hasMother = seededRelationships.includes('MOTHER')
  const hasFather = seededRelationships.includes('FATHER')

  // GEN-3393: seed parent `comparators[]` directly (the single source of
  // truth). This payload is PATCHed straight to the session (not through
  // `setCanonicalOrder`), so the comparator rows must be emitted at the source.
  // Incidental findings is never seeded (matches the proband above) — the
  // provider must explicitly choose, so `null` is the "unanswered" sentinel.
  const comparators: Comparator[] = []
  if (hasMother)
    comparators.push({
      relationship: 'MOTHER',
      isPrimaryGuardian: true,
      secondaryConsent: { includeIncidentalFindings: null },
    })
  if (hasFather)
    comparators.push({
      relationship: 'FATHER',
      isPrimaryGuardian: !hasMother,
      secondaryConsent: { includeIncidentalFindings: null },
    })

  // P3 (MOTHER/FATHER migration): derive the tier from the comparators we
  // just emitted, so the boolean-pair input is purely a UI seed and the
  // single source of truth for `orderType` is the comparator set.
  const orderType = orderTypeFromComparators({
    schemaVersion: CANONICAL_SCHEMA_VERSION,
    orderType: 'PROBAND_ONLY',
    comparators: comparators.length > 0 ? comparators : null,
  } as CanonicalOrderPayload)

  return {
    schemaVersion: CANONICAL_SCHEMA_VERSION,
    orderType,
    patient,
    institution,
    providers,
    order: {
      selectedOption: testOptions.testType || null,
      labName: labName?.trim() ? labName.trim() : null,
      encounterCsn: null,
    },
    family: { familyHistory: null, hasParent: null, hasGrandparent: null, hasSibling: null, hasCousin: null },
    comparators: comparators.length > 0 ? comparators : null,
    reason: { icd10Diagnosis: null, userInteracted: false, clinicalDiagnosis: [] },
    clinical: { clinicalNote: null, differentialDiagnosis: null, priorGeneticTesting: null },
    primaryConsent: null,
    ehrContext: ehrContext ?? null,
    version: 1,
  }
}

/**
 * Overlay user-entered draft content from a Redux-held canonical (`draft`) onto a
 * `base` canonical (a freshly-initialized one, or a server-stored session payload),
 * keeping the base's FHIR-sourced patient / institution / providers while carrying
 * forward the draft's content fields so a resumed draft is not silently discarded.
 *
 * `freshLabName` is the lab name resolved for the current launch; it wins over the
 * draft's persisted lab name. Used by OrderStart's create-session flow for both the
 * brand-new-session and resume-existing-session cases, which previously inlined two
 * identical merge blocks. GEN-3393: `comparators[]` is the single source of truth
 * for parent/sibling rows, so it is carried forward whenever the draft defines it.
 */
export function carryForwardDraftContentOntoCanonical(
  base: CanonicalOrderPayload,
  draft: CanonicalOrderPayload,
  freshLabName?: string | null
): CanonicalOrderPayload {
  return {
    ...base,
    orderType: draft.orderType ?? base.orderType,
    orderingSite: draft.orderingSite !== undefined ? draft.orderingSite : base.orderingSite,
    order: draft.order ? { ...draft.order, labName: freshLabName ?? draft.order.labName ?? null } : base.order,
    reason: draft.reason ?? base.reason,
    clinical: draft.clinical ?? base.clinical,
    family: draft.family ?? base.family,
    comparators: draft.comparators !== undefined ? draft.comparators : base.comparators,
    guardianContact: draft.guardianContact !== undefined ? draft.guardianContact : base.guardianContact,
  }
}

/**
 * Convert a legacy IMDB `json_payload` (stored as `{orderFormInfo, requisitionFormData}`)
 * to a CanonicalOrderPayload suitable for canonicalOrderSlice.
 *
 * Used by the legacy IMDB load path (DatabaseOperations / RequisitionForm) to import
 * old orders into the canonical-first session flow.
 */
export function legacyTrfPayloadToCanonical(
  jsonPayload: { orderFormInfo?: OrderFormInfoData | null; requisitionFormData: PartialRequisitionFormData },
  ehrContext?: CanonicalEhrContext | null
): CanonicalOrderPayload {
  const { orderFormInfo, requisitionFormData: rawRfd } = jsonPayload
  const requisitionFormData = normalizeImdbRequisitionPartial(rawRfd)

  // Merge partial TRF with minimal defaults to satisfy mapTrfToCanonical's full RequisitionFormData signature
  const fullReqFormData: RequisitionFormData = {
    order: { selectedOption: TestType.NONE, lab_name: '', ...(requisitionFormData.order ?? {}) },
    patient: {
      firstName: '',
      lastName: '',
      middleName: '',
      dateOfBirth: '',
      sex: Sex.Unknown,
      mrn: '',
      collectionType: CollectionType.Unknown,
      ...(requisitionFormData.patient ?? {}),
    },
    clinical: { clinicalNote: '', ...(requisitionFormData.clinical ?? {}) },
    reason: { clinicalDiagnosis: [], icd10Diagnosis: '', userInteracted: false, ...(requisitionFormData.reason ?? {}) },
    family: {
      familyHistory: '',
      hasSibling: false,
      hasParent: false,
      hasGrandparent: false,
      hasCousin: false,
      ...(requisitionFormData.family ?? {}),
    },
    mother: requisitionFormData.mother as ParentFieldShape | undefined,
    father: requisitionFormData.father as ParentFieldShape | undefined,
    consent: {
      providerName: '',
      providerEmail: '',
      envelopeId: '',
      status: '',
      isSigningComplete: false,
      ...(requisitionFormData.consent ?? {}),
    },
    consentFormData: requisitionFormData.consentFormData as ConsentFormData | undefined,
    version: 1,
    guardianContact: requisitionFormData.guardianContact ?? undefined,
    primaryConsentFamilyMember: requisitionFormData.primaryConsentFamilyMember ?? undefined,
    consentFlowId: requisitionFormData.consentFlowId ?? undefined,
    primaryConsentKey: requisitionFormData.primaryConsentKey ?? undefined,
  }

  const safeOrderFormInfo: OrderFormInfoData = orderFormInfo ?? ({} as OrderFormInfoData)
  return mapTrfToCanonical(safeOrderFormInfo, fullReqFormData, ehrContext ?? null)
}
